import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  getLeads, updateLead, getLeadNotes, getStats,
  getTargets, updateTarget, addManualTarget, previewCsvUpload,
  confirmCsvUpload, downloadCsvTemplate,
  getSettings, updateSettings,
  getPipelineJobs, triggerJob, getPipelineRuns, getPipelineRun,
  getPipelineStatus, getSchedules, updateSchedule
} from "./api";

// ─── CONSTANTS ────────────────────────────────────────────────────────────

const TC = {
  hot: { icon: "🔥", badge: "bg-[#FEF2F2] text-[#B91C1C] border-[#FCA5A5]" },
  warm: { icon: "⚡", badge: "bg-[#FFFBEB] text-[#92400E] border-[#FCD34D]" },
  cold: { icon: "🧊", badge: "bg-[#EBF7F4] text-[#0D8A72] border-[#9FD9CE]" }
};

const SC = {
  legacy_cms: { c: "text-[#B91C1C]", i: "⚠" },
  leadership_change: { c: "text-[#6B21A8]", i: "👤" },
  compliance_pressure: { c: "text-[#9A3412]", i: "⚖" },
  b2b_rebrand: { c: "text-[#BE185D]", i: "🎨" },
  org_change: { c: "text-[#92400E]", i: "🔀" },
  funding_event: { c: "text-[#166534]", i: "💰" },
  b2b_growth_signal: { c: "text-[#0D8A72]", i: "📈" },
  strategy_announced: { c: "text-[#0E7490]", i: "🗺" },
  b2b_expansion: { c: "text-[#0A6B58]", i: "🌍" },
  job_posting: { c: "text-[#3730A3]", i: "💼" },
  poor_performance: { c: "text-[#C2410C]", i: "🐢" },
  pain_signal: { c: "text-[#9A3412]", i: "😤" },
  analyst_recognition: { c: "text-[#7C3AED]", i: "🏆" }
};

const SRC = {
  news: { bg: "bg-[#EBF7F4]", text: "text-[#0A6B58]", border: "border-[#9FD9CE]", label: "News" },
  job: { bg: "bg-[#EEF2FF]", text: "text-[#3730A3]", border: "border-[#C7D2FE]", label: "Job Posting" },
  wappalyzer: { bg: "bg-[#F7FAFA]", text: "text-[#2D3A38]", border: "border-[#C8DAD6]", label: "Tech Scan" },
  pagespeed: { bg: "bg-[#FFF7ED]", text: "text-[#9A3412]", border: "border-[#FDBA74]", label: "PageSpeed" }
};

const VL = {
  b2b_saas: "B2B SaaS", b2b_manufacturing: "Manufacturing",
  higher_education: "Higher Ed", healthcare: "Healthcare",
  nonprofit: "Nonprofit", media_publishing: "Media",
  financial_services: "Finance", b2b_professional_services: "Prof. Services"
};

const VERTS = [
  { id: "b2b_saas", l: "B2B SaaS" },
  { id: "b2b_professional_services", l: "B2B Prof. Services" },
  { id: "b2b_manufacturing", l: "B2B Manufacturing" },
  { id: "higher_education", l: "Higher Education" },
  { id: "healthcare", l: "Healthcare" },
  { id: "nonprofit", l: "Nonprofit / Association" },
  { id: "media_publishing", l: "Media / Publishing" },
  { id: "financial_services", l: "Financial Services" }
];

const DAYS = [
  { id: "monday", s: "Mo" }, { id: "tuesday", s: "Tu" },
  { id: "wednesday", s: "We" }, { id: "thursday", s: "Th" },
  { id: "friday", s: "Fr" }, { id: "saturday", s: "Sa" },
  { id: "sunday", s: "Su" }
];

const TIMES = Array.from({ length: 48 }, (_, i) => {
  const h = Math.floor(i / 2);
  const m = i % 2 === 0 ? "00" : "30";
  const sf = h < 12 ? "AM" : "PM";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return { v: String(h).padStart(2, "0") + ":" + m, l: h12 + ":" + m + " " + sf };
});

const JOB_ORDER = ["full_pipeline", "news_monitor", "highered_news_monitor", "job_monitor", "wappalyzer_scan", "pagespeed_scan", "podcast_monitor", "contact_enricher", "company_enricher", "scoring", "ipeds_import"];

const SIG_LABELS = {
  legacy_cms: "Legacy CMS", leadership_change: "Leadership Change",
  compliance_pressure: "Compliance Pressure", b2b_rebrand: "B2B Rebrand",
  org_change: "Org Change", funding_event: "Funding Event",
  b2b_growth_signal: "B2B Growth Signal", strategy_announced: "Strategy Announced",
  b2b_expansion: "B2B Expansion", job_posting: "Job Posting",
  poor_performance: "Poor Performance", pain_signal: "Pain Signal",
  analyst_recognition: "Analyst Recognition"
};

const RS = {
  completed: { b: "bg-[#F0FDF4] text-[#166534] border-green-900", d: "bg-green-400" },
  running: { b: "bg-[#EBF7F4] text-[#0D8A72] border-[#9FD9CE]", d: "bg-[#0D8A72] animate-pulse" },
  failed: { b: "bg-[#FEF2F2] text-[#B91C1C] border-[#FCA5A5]", d: "bg-red-400" },
  pending: { b: "bg-[#F7FAFA] text-[#6B8480] border-[#C8DAD6]", d: "bg-gray-500" }
};

const TS = {
  active: "bg-[#F0FDF4] text-[#166534] border-green-900",
  pending_review: "bg-[#FAF5FF] text-[#6B21A8] border-purple-900",
  excluded: "bg-[#FEF2F2] text-[#B91C1C] border-[#FCA5A5]",
  contacted: "bg-[#EBF7F4] text-[#0D8A72] border-[#9FD9CE]",
  paused: "bg-[#FFFBEB] text-[#92400E] border-[#FCD34D]",
  converted: "bg-emerald-950 text-emerald-400 border-emerald-900"
};

// ─── HELPERS ──────────────────────────────────────────────────────────────

function ago(d) {
  if (d === null || d === undefined) return "Never";
  if (typeof d === "number") {
    if (d < 1) return "Today";
    if (d === 1) return "Yesterday";
    return d + "d ago";
  }
  const diff = Math.floor((Date.now() - new Date(d)) / 1000);
  if (diff < 60) return diff + "s ago";
  if (diff < 3600) return Math.floor(diff / 60) + "m ago";
  if (diff < 86400) return Math.floor(diff / 3600) + "h ago";
  return Math.floor(diff / 86400) + "d ago";
}

function dur(s) {
  if (!s) return null;
  if (s < 60) return s + "s";
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r ? m + "m " + r + "s" : m + "m";
}

function sdesc(f) {
  if (!f.enabled) return "Disabled";
  const found = TIMES.find(x => x.v === f.time);
  const t = found ? found.l : f.time;
  const freq = f.frequency || f.freq;
  if (freq === "daily") return "Every day at " + t;
  if (freq === "weekdays") return "Mon-Fri at " + t;
  const days = f.days || [];
  const ds = DAYS.filter(d => days.includes(d.id)).map(d => d.id[0].toUpperCase() + d.id.slice(1));
  if (!ds.length) return "Select days";
  if (ds.length === 1) return "Every " + ds[0] + " at " + t;
  return ds.slice(0, -1).join(", ") + " and " + ds[ds.length - 1] + " at " + t;
}

// ─── SMALL COMPONENTS ─────────────────────────────────────────────────────

function Badge({ tier }) {
  const c = TC[tier];
  if (!c) return null;
  return (
    <span className={"inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded border " + c.badge}>
      {c.icon} {tier}
    </span>
  );
}

function RBadge({ status }) {
  const s = RS[status] || RS.pending;
  return (
    <span className={"inline-flex items-center gap-1.5 px-2 py-0.5 text-xs rounded border " + s.b}>
      <span className={"w-1.5 h-1.5 rounded-full " + s.d} />
      {status}
    </span>
  );
}

function Tog({ on, set }) {
  return (
    <button
      onClick={() => set(!on)}
      className={"relative w-9 h-5 rounded-full transition-colors shrink-0 " + (on ? "bg-[#0D8A72]" : "bg-[#EDF2F1]")}
    >
      <span className={"absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform " + (on ? "translate-x-4" : "translate-x-0.5")} />
    </button>
  );
}

function Box({ title, desc, children }) {
  return (
    <div className="border border-[#D8E5E2] rounded-lg p-5">
      <p className="text-sm font-medium text-[#111] mb-1">{title}</p>
      {desc && <p className="text-xs text-[#8AA09C] mb-4">{desc}</p>}
      {children}
    </div>
  );
}

function Tags({ tags, set, ph }) {
  const [v, setV] = useState("");
  function add() {
    const t = v.trim();
    if (!t || tags.includes(t)) return;
    set([...tags, t]);
    setV("");
  }
  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {tags.map(t => (
          <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-[#F7FAFA] text-[#2D3A38] rounded border border-[#C8DAD6]">
            {t}
            <button onClick={() => set(tags.filter(x => x !== t))} className="text-[#9AADA9] hover:text-[#2D3A38] ml-0.5">x</button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input value={v} onChange={e => setV(e.target.value)} onKeyDown={e => e.key === "Enter" && add()} placeholder={ph}
          className="flex-1 px-3 py-1.5 text-sm bg-white border border-[#C8DAD6] rounded text-[#2D3A38] placeholder-[#9AADA9] focus:outline-none focus:border-[#B0C8C4]" />
        <button onClick={add} disabled={!v.trim()} className="px-3 py-1.5 text-sm border border-[#C8DAD6] text-[#6B8480] rounded hover:text-[#111] disabled:opacity-30">Add</button>
      </div>
    </div>
  );
}

function Spinner() {
  return <div className="flex items-center justify-center py-12"><div className="w-6 h-6 border-2 border-[#C8DAD6] border-t-blue-500 rounded-full animate-spin" /></div>;
}

// ─── SIGNAL CARD ──────────────────────────────────────────────────────────

function SignalCard({ signal, expanded, onToggle }) {
  const c = SC[signal.type] || {};
  const st = SRC[signal.source_type] || SRC.news;
  return (
    <div
      className={"rounded-lg border overflow-hidden cursor-pointer transition-all " + (expanded ? "border-[#B0C8C4] bg-[#F7FAFA]/50" : "border-[#D8E5E2] hover:border-[#C8DAD6] hover:bg-white/50")}
      onClick={onToggle}
    >
      <div className="px-3 py-2.5">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 mb-0.5">
              <span className={"text-xs font-medium " + (c.c || "text-[#6B8480]")}>
                {c.i} {signal.type.replace(/_/g, " ").replace(/\b\w/g, x => x.toUpperCase())}
              </span>
              {signal.weight !== signal.raw_weight && <span className="text-xs text-[#9AADA9]">(decayed)</span>}
            </div>
            <p className="text-xs text-[#6B8480] leading-relaxed">{signal.detail}</p>
            <div className="flex items-center gap-2 mt-1.5">
              <span className={"text-xs px-1.5 py-0.5 rounded border " + st.bg + " " + st.text + " " + st.border}>{st.label}</span>
              <span className="text-xs text-[#9AADA9]">{signal.days_ago}d ago</span>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0 pt-0.5">
            <span className="text-xs text-[#6B8480] font-medium">+{signal.weight}</span>
            <span className="text-xs text-[#8AA09C]">{expanded ? "▲" : "▼"}</span>
          </div>
        </div>
      </div>
      {expanded && (
        <div className="px-3 pb-3 pt-2 border-t border-[#C8DAD6] space-y-2.5" onClick={e => e.stopPropagation()}>
          <p className="text-xs text-[#2D3A38] leading-relaxed">{signal.source_detail}</p>
          <div className="flex items-center justify-between">
            <span className="text-xs text-[#8AA09C]">Source: <span className="text-[#6B8480]">{signal.source_label}</span></span>
            <a href={signal.source_url} target="_blank" rel="noreferrer"
              className="text-xs text-[#0D8A72] hover:text-[#0A6B58] px-2 py-1 rounded bg-[#EBF7F4]/50 border border-[#9FD9CE]/50 hover:bg-[#EBF7F4] transition-colors">
              View source
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── LEAD DETAIL ──────────────────────────────────────────────────────────

function Detail({ lead, close, onAction }) {
  const [note, setNote] = useState("");
  const [notes, setNotes] = useState([]);
  const [expandedSig, setExpandedSig] = useState(null);
  const [loadingNotes, setLoadingNotes] = useState(false);

  useEffect(() => {
    setExpandedSig(null);
    setLoadingNotes(true);
    getLeadNotes(lead.id).then(data => {
      setNotes(Array.isArray(data) ? data : []);
      setLoadingNotes(false);
    }).catch(() => setLoadingNotes(false));
  }, [lead.id]);

  async function saveNote() {
    if (!note.trim()) return;
    await updateLead(lead.id, { status: lead.status, note: note.trim() });
    setNotes(n => [{ id: Date.now(), note: note.trim(), created_at: new Date().toISOString() }, ...n]);
    setNote("");
  }

  const { company, signals, combos, firm, score, tier, status } = lead;

  return (
    <div className="border-t-2 border-[#0D8A72] bg-[#F7FAFA] p-4">
      {/* Header row */}
      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-2xl font-bold text-[#111]">{score}</span>
          <Badge tier={tier} />
          <span className="text-xs text-[#8AA09C] capitalize">{status}</span>
          {company.domain && company.domain !== "unknown"
            ? <a href={"https://" + company.domain} target="_blank" rel="noreferrer"
                className="text-xs text-[#0D8A72] hover:underline">{company.domain}</a>
            : <span className="text-xs text-[#9AADA9] italic">domain unknown</span>
          }
        </div>
        <div className="flex gap-2 items-center shrink-0">
          {status === "dismissed" ? (
            <button onClick={() => onAction(lead.id, "new")}
              className="px-3 py-1.5 text-xs rounded bg-[#0D8A72] text-white hover:bg-[#0A6B58] transition-colors">
              ↩ Restore
            </button>
          ) : (
            <>
              <button onClick={() => onAction(lead.id, "contacted")}
                className="px-3 py-1.5 text-xs rounded bg-[#0D8A72] text-white hover:bg-[#0A6B58] transition-colors">
                Contacted
              </button>
              <button onClick={() => onAction(lead.id, "dismissed")}
                className="px-3 py-1.5 text-xs rounded bg-white text-[#6B8480] border border-[#C8DAD6] hover:bg-[#EDF2F1] transition-colors">
                Dismiss
              </button>
            </>
          )}
          <button onClick={close} className="text-[#9AADA9] hover:text-[#2D3A38] text-base ml-1">✕</button>
        </div>
      </div>

      {/* 3-column content grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">

        {/* Col 1 — Company info */}
        <div>
          <p className="text-xs font-medium text-[#9AADA9] uppercase tracking-wider mb-2">Company</p>
          {[
            ["Vertical", VL[company.industry] || company.industry],
            company.industry === "higher_education"
              ? ["Students", company.enrollment ? company.enrollment.toLocaleString() : null]
              : ["Employees", company.employee_count ? company.employee_count.toLocaleString() : null],
            company.industry === "higher_education"
              ? ["Type", company.institution_type ? company.institution_type.replace(/_/g, " ").replace(/\b\w/g, x => x.toUpperCase()) : null]
              : ["Revenue", company.revenue_estimate],
            ["Location", company.location],
          ].filter(([, v]) => v).map(([label, value]) => (
            <div key={label} className="flex justify-between py-1 border-b border-[#EDF2F1]">
              <span className="text-xs text-[#9AADA9]">{label}</span>
              <span className="text-xs text-[#2D3A38] text-right ml-2">{value}</span>
            </div>
          ))}
          {firm && firm.length > 0 && (
            <div className="mt-3">
              <p className="text-xs font-medium text-[#9AADA9] uppercase tracking-wider mb-2">Firmographic</p>
              {firm.map((m, i) => (
                <div key={i} className="flex justify-between py-0.5">
                  <span className="text-xs text-[#8AA09C]">{m.detail}</span>
                  <span className={"text-xs font-medium " + (m.value > 0 ? "text-[#166534]" : "text-[#B91C1C]")}>{m.value > 0 ? "+" : ""}{m.value}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Col 2 — Signals + Combos */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-medium text-[#9AADA9] uppercase tracking-wider">Signals</p>
            <span className="text-xs text-[#8AA09C] italic">click to see source</span>
          </div>
          <div className="space-y-2">
            {(signals || []).map((s, i) => (
              <SignalCard key={i} signal={s} expanded={expandedSig === i} onToggle={() => setExpandedSig(expandedSig === i ? null : i)} />
            ))}
          </div>
          {combos && combos.length > 0 && (
            <div className="mt-3">
              <p className="text-xs font-medium text-[#9AADA9] uppercase tracking-wider mb-2">Combination Bonuses</p>
              <div className="space-y-1.5">
                {combos.map((b, i) => (
                  <div key={i} className="flex justify-between gap-2 bg-[#F0FDF4] rounded px-2.5 py-1.5 border border-[#C8DAD6]">
                    <p className="text-xs text-[#6B8480]">{b.description}</p>
                    <span className="text-xs text-[#166534] shrink-0 font-medium">+{b.bonus}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Col 3 — Contacts, Snooze, Notes */}
        <div className="space-y-4">
          {company.contacts && company.contacts.length > 0 && (
            <div>
              <p className="text-xs font-medium text-[#9AADA9] uppercase tracking-wider mb-2">Contacts</p>
              <div className="space-y-2">
                {company.contacts.map((c, i) => (
                  <div key={i} className="bg-white rounded p-2 border border-[#C8DAD6]">
                    <p className="text-xs text-[#111] font-medium">{c.name}</p>
                    <p className="text-xs text-[#8AA09C]">{c.title}</p>
                    <a href={"mailto:" + c.email} className="text-xs text-[#0D8A72] hover:underline">{c.email}</a>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div>
            <p className="text-xs font-medium text-[#9AADA9] uppercase tracking-wider mb-2">Snooze</p>
            <div className="flex gap-1.5 flex-wrap">
              {[7, 14, 30, 60].map(d => (
                <button key={d} onClick={() => onAction(lead.id, "new", { snooze_days: d })}
                  className="px-2.5 py-1 text-xs rounded bg-white text-[#6B8480] border border-[#C8DAD6] hover:bg-[#EDF2F1] transition-colors">{d}d</button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-medium text-[#9AADA9] uppercase tracking-wider mb-2">Notes</p>
            {loadingNotes ? <p className="text-xs text-[#9AADA9] mb-2">Loading...</p> : notes.length === 0 ? (
              <p className="text-xs text-[#9AADA9] mb-2">No notes yet</p>
            ) : (
              <div className="space-y-1.5 mb-2 max-h-32 overflow-y-auto">
                {notes.map(n => (
                  <div key={n.id} className="text-xs bg-white rounded p-2 border border-[#C8DAD6]">
                    <p className="text-[#2D3A38]">{n.note}</p>
                    {n.created_at && <p className="text-[#9AADA9] mt-0.5">{new Date(n.created_at).toLocaleDateString()}</p>}
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <input value={note} onChange={e => setNote(e.target.value)} onKeyDown={e => e.key === "Enter" && saveNote()}
                placeholder="Add a note..."
                className="flex-1 px-2.5 py-1.5 text-xs bg-white border border-[#C8DAD6] rounded text-[#2D3A38] placeholder-[#9AADA9] focus:outline-none focus:border-[#B0C8C4]" />
              <button onClick={saveNote} disabled={!note.trim()}
                className="px-3 py-1.5 text-xs bg-[#0D8A72] text-white rounded hover:bg-[#0A6B58] disabled:opacity-30 transition-colors">Save</button>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

// ─── LEADS TAB ────────────────────────────────────────────────────────────

function SortIcon({ col, sortCol, sortDir }) {
  if (sortCol !== col) return <span className="text-[#9AADA9] ml-1">⇅</span>;
  return <span className="text-[#0D8A72] ml-1">{sortDir === "asc" ? "↑" : "↓"}</span>;
}

function LeadsTab() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tf, setTf] = useState(null);
  const [vf, setVf] = useState(null);
  const [sf, setSf] = useState(null);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [sortCol, setSortCol] = useState("score");
  const [sortDir, setSortDir] = useState("desc");

  const fetchLeads = useCallback(() => {
    setLoading(true);
    const params = { page_size: 2000 };
    if (tf === "dismissed") {
      params.status = "dismissed";
    } else {
      if (tf) params.tier = tf;
      params.exclude_status = "dismissed";
    }
    if (vf) params.vertical = vf;
    if (sf) params.signal_type = sf;
    if (q) params.search = q;
    getLeads(params).then(data => {
      setLeads(data.leads || data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [tf, vf, sf, q]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  async function handleAction(id, status, extra) {
    // Save scroll position before update
    const scrollY = window.scrollY;
    await updateLead(id, { status, ...extra });
    // Update leads in place without full refetch to preserve scroll
    setLeads(prev => prev.filter(l => {
      if (l.id !== id) return true;
      // Keep dismissed leads visible only if showing dismissed tab
      if (status === "dismissed") return tf === "dismissed";
      return true;
    }));
    // Update the selected lead's status if it's open
    if (sel && sel.id === id) {
      if (status === "dismissed") {
        setSel(null);
      } else {
        setSel(prev => prev ? { ...prev, status } : null);
      }
    }
    // Restore scroll position after React re-renders
    requestAnimationFrame(() => window.scrollTo(0, scrollY));
    // Full refetch in background to sync
    fetchLeads();
  }

  function handleSort(col) {
    if (sortCol === col) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortCol(col);
      setSortDir("desc");
    }
  }

  function getLegacyCms(lead) {
    const sig = (lead.signals || []).find(s => s.type === "legacy_cms");
    if (!sig) return null;
    const raw = sig.source_detail || sig.detail || "";
    // Extract just the CMS name from the detail string
    const match = raw.match(/^(Drupal \d+|WordPress[\s0-9.]*|Joomla|Unknown\/Bespoke)/i);
    return match ? match[1].trim() : raw.split(" ")[0] + " " + (raw.split(" ")[1] || "");
  }

  function getLegacyCmsWeight(lead) {
    const sig = (lead.signals || []).find(s => s.type === "legacy_cms");
    return sig ? sig.weight : 0;
  }

  const sorted = [...leads].sort((a, b) => {
    let av, bv;
    switch (sortCol) {
      case "company":
        av = a.company.name || ""; bv = b.company.name || "";
        return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      case "vertical":
        av = VL[a.company.industry] || a.company.industry || ""; bv = VL[b.company.industry] || b.company.industry || "";
        return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      case "legacy_cms":
        av = getLegacyCmsWeight(a); bv = getLegacyCmsWeight(b);
        return sortDir === "asc" ? av - bv : bv - av;
      case "employees":
        av = a.company.employee_count || 0; bv = b.company.employee_count || 0;
        return sortDir === "asc" ? av - bv : bv - av;
      case "revenue":
        av = a.company.revenue_estimate || ""; bv = b.company.revenue_estimate || "";
        return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      case "endowment":
        av = a.company.revenue_estimate || ""; bv = b.company.revenue_estimate || "";
        return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      case "tier": {
        const tierOrder = { hot: 3, warm: 2, cold: 1 };
        av = tierOrder[a.tier] || 0; bv = tierOrder[b.tier] || 0;
        return sortDir === "asc" ? av - bv : bv - av;
      }
      case "score":
      default:
        av = a.score || 0; bv = b.score || 0;
        return sortDir === "asc" ? av - bv : bv - av;
    }
  });

  const ct = {
    all: leads.length,
    hot: leads.filter(l => l.tier === "hot").length,
    warm: leads.filter(l => l.tier === "warm").length,
    cold: leads.filter(l => l.tier === "cold").length,
  };

  const isHigherEd = (lead) => lead.company.industry === "higher_education";

  function getEndowment(lead) {
    const sig = (lead.signals || []).find(s =>
      s.raw_data && (s.raw_data.endowment || s.raw_data.endowment_display)
    );
    if (!sig) return null;
    if (sig.raw_data.endowment_display) return sig.raw_data.endowment_display;
    const val = sig.raw_data.endowment;
    if (!val) return null;
    if (val >= 1_000_000_000) return "$" + (val / 1_000_000_000).toFixed(1) + "B";
    if (val >= 1_000_000) return "$" + Math.round(val / 1_000_000) + "M";
    return "$" + val.toLocaleString();
  }

  function getSizeDisplay(lead) {
    if (isHigherEd(lead)) {
      const enrollment = lead.company.enrollment;
      if (enrollment) return {
        value: enrollment,
        label: enrollment.toLocaleString(),
        sublabel: "students"
      };
      return null;
    }
    const emp = lead.company.employee_count;
    if (emp) return {
      value: emp,
      label: emp.toLocaleString(),
      sublabel: "employees"
    };
    return null;
  }

  const COLS = [
    { key: "company", label: "Company", align: "left" },
    { key: "vertical", label: "Vertical", align: "left" },
    { key: "legacy_cms", label: "Legacy CMS", align: "left" },
    { key: "signals", label: "Other Signals", align: "left", nosort: true },
    { key: "employees", label: "Employees / Students", align: "right" },
    { key: "endowment", label: "Endowment / Revenue", align: "left" },
    { key: "tier", label: "Tier", align: "left" },
    { key: "score", label: "Score", align: "right" },
    { key: "actions", label: "", align: "right", nosort: true },
  ];

  function exportLeadsCsv() {
    const headers = [
      "Company", "Domain", "Vertical", "Location",
      "Legacy CMS", "Other Signals", "Employees / Students",
      "Endowment / Revenue", "Tier", "Score", "Status"
    ];
    const rows = sorted.map(lead => {
      const legacyCms = getLegacyCms(lead);
      const otherSignals = (lead.signals || [])
        .filter(s => s.type !== "legacy_cms")
        .map(s => s.type.replace(/_/g, " "))
        .join("; ");
      const sizeDisplay = getSizeDisplay(lead);
      const endowment = getEndowment(lead);
      const higherEd = isHigherEd(lead);
      return [
        `"${(lead.company.name || "").replace(/"/g, '""')}"`,
        lead.company.domain || "",
        VL[lead.company.industry] || lead.company.industry || "",
        `"${(lead.company.location || "").replace(/"/g, '""')}"`,
        `"${(legacyCms || "").replace(/"/g, '""')}"`,
        `"${otherSignals.replace(/"/g, '""')}"`,
        sizeDisplay ? `${sizeDisplay.label} ${sizeDisplay.sublabel}` : "",
        higherEd ? (endowment || "") : (lead.company.revenue_estimate || ""),
        lead.tier || "",
        lead.score || "",
        lead.status || "",
      ].join(",");
    });

    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const date = new Date().toISOString().split("T")[0];
    a.href = url;
    a.download = `leads-${date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-3 items-center">
          <div className="flex rounded-lg border border-[#D8E5E2] overflow-hidden">
            {[
              { id: null, l: "All (" + ct.all + ")" },
              { id: "hot", l: "🔥 Hot (" + ct.hot + ")" },
              { id: "warm", l: "⚡ Warm (" + ct.warm + ")" },
              { id: "cold", l: "🧊 Cold (" + ct.cold + ")" },
              { id: "dismissed", l: "✕ Dismissed" },
            ].map(t => (
              <button key={String(t.id)} onClick={() => setTf(t.id)}
                className={"px-2 sm:px-3 py-1.5 text-xs transition-colors " + (tf === t.id ? "bg-[#0D8A72] text-[#111]" : "text-[#6B8480] hover:text-[#111] hover:bg-[#F7FAFA]")}>{t.l}</button>
            ))}
          </div>
          <select value={vf || ""} onChange={e => setVf(e.target.value || null)} className="px-2 py-1.5 text-xs bg-white border border-[#D8E5E2] rounded text-[#2D3A38] focus:outline-none flex-1 sm:flex-none">
            <option value="">All Verticals</option>
            {VERTS.map(v => <option key={v.id} value={v.id}>{v.l}</option>)}
          </select>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search..."
            className="px-3 py-1.5 text-xs bg-white border border-[#D8E5E2] rounded text-[#2D3A38] placeholder-[#9AADA9] focus:outline-none flex-1 sm:w-36 sm:flex-none" />
          {(sf || vf || tf) && (
            <button onClick={() => { setSf(null); setVf(null); setTf(null); setQ(""); }}
              className="text-xs text-[#0D8A72] hover:text-[#0A6B58] transition-colors">Clear</button>
          )}
          <button onClick={exportLeadsCsv}
            className="px-3 py-1.5 text-xs border border-[#C8DAD6] text-[#6B8480] hover:text-[#111] rounded transition-colors">
            ↓ Export CSV
          </button>
          <span className="text-xs text-[#9AADA9] ml-auto">{leads.length} leads</span>
        </div>

        {/* Signal filter row */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          <span className="text-xs text-[#9AADA9] self-center mr-1">Signal:</span>
          <button onClick={() => setSf(null)}
            className={"px-2 py-1 text-xs rounded border transition-colors " + (!sf ? "bg-[#E6F6F3] border-[#9FD9CE] text-[#0A6B58]" : "border-[#D8E5E2] text-[#9AADA9] hover:text-[#6B8480] hover:border-[#C8DAD6]")}>
            All
          </button>
          {Object.entries(SC).map(([key, val]) => (
            <button key={key} onClick={() => setSf(sf === key ? null : key)}
              className={"px-2 py-1 text-xs rounded border transition-colors " + (sf === key ? "bg-[#E6F6F3] border-[#9FD9CE] text-[#0A6B58]" : "border-[#D8E5E2] text-[#9AADA9] hover:text-[#6B8480] hover:border-[#C8DAD6]")}>
              {val.i} {key.replace(/_/g, " ").replace(/\b\w/g, x => x.toUpperCase())}
            </button>
          ))}
        </div>

        <div className="border border-[#D8E5E2] rounded-lg overflow-hidden">
          {/* Desktop table */}
          <div className="hidden sm:block">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#D8E5E2] bg-white/50">
                {COLS.map(col => (
                  <th
                    key={col.key}
                    onClick={() => !col.nosort && handleSort(col.key)}
                    className={
                      "px-4 py-2.5 text-xs text-[#8AA09C] font-medium " +
                      (col.align === "right" ? "text-right" : "text-left") +
                      (!col.nosort ? " cursor-pointer hover:text-[#2D3A38] select-none" : "")
                    }
                  >
                    {col.label}
                    {!col.nosort && <SortIcon col={col.key} sortCol={sortCol} sortDir={sortDir} />}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-[#9AADA9] text-sm">Loading leads...</td></tr>
              ) : sorted.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-[#9AADA9] text-sm">No leads match your filters</td></tr>
              ) : sorted.map(lead => {
                const is = sel && sel.id === lead.id;
                const legacyCms = getLegacyCms(lead);
                const otherSignals = (lead.signals || []).filter(s => s.type !== "legacy_cms");
                const sizeDisplay = getSizeDisplay(lead);
                const endowment = getEndowment(lead);
                const higherEd = isHigherEd(lead);
                let cmsColor = "text-[#8AA09C]";
                const cmsLower = (legacyCms || "").toLowerCase();
                if (cmsLower.includes("drupal 7") || cmsLower.includes("drupal 6")) cmsColor = "text-[#B91C1C]";
                else if (cmsLower.includes("drupal 8") || cmsLower.includes("drupal 9")) cmsColor = "text-[#9A3412]";
                else if (cmsLower.includes("wordpress")) cmsColor = "text-[#92400E]";
                else if (cmsLower.includes("joomla")) cmsColor = "text-[#C2410C]";
                return (
                  <React.Fragment key={lead.id}>
                    <tr onClick={() => setSel(is ? null : lead)}
                      className={"border-b border-[#D8E5E2]/50 cursor-pointer transition-colors " + (is ? "bg-[#EBF7F4]" : "hover:bg-[#F7FAFA]")}>
                      <td className="px-4 py-3">
                        <p className="font-medium text-[#111] text-sm">{lead.company.name}</p>
                        <p className="text-xs text-[#8AA09C]">{lead.company.domain === "unknown" ? "domain unknown" : lead.company.domain}{lead.company.location ? " · " + lead.company.location : ""}</p>
                        {lead.status !== "new" && <span className="text-xs text-[#9AADA9] capitalize">{lead.status}</span>}
                      </td>
                      <td className="px-4 py-3 text-xs text-[#6B8480]">{VL[lead.company.industry] || lead.company.industry || "—"}</td>
                      <td className="px-4 py-3">
                        {legacyCms ? <span className={"text-xs font-medium " + cmsColor}>⚠ {legacyCms}</span> : <span className="text-xs text-[#9AADA9]">—</span>}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {otherSignals.slice(0, 2).map((s, i) => {
                          const c = SC[s.type] || {};
                          return <span key={i} className={(c.c || "text-[#6B8480]") + " mr-2"}>{c.i} {s.type.replace(/_/g, " ").replace(/\b\w/g, x => x.toUpperCase())}</span>;
                        })}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {sizeDisplay ? <div><span className="text-xs text-[#2D3A38]">{sizeDisplay.label}</span><p className="text-xs text-[#9AADA9]">{sizeDisplay.sublabel}</p></div> : <span className="text-xs text-[#9AADA9]">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        {higherEd ? (endowment ? <div><span className="text-xs text-[#0D8A72]">{endowment}</span><p className="text-xs text-[#9AADA9]">endowment</p></div> : <span className="text-xs text-[#9AADA9]">—</span>)
                          : (lead.company.revenue_estimate ? <div><span className="text-xs text-[#6B8480]">{lead.company.revenue_estimate}</span><p className="text-xs text-[#9AADA9]">est. revenue</p></div> : <span className="text-xs text-[#9AADA9]">—</span>)}
                      </td>
                      <td className="px-4 py-3"><Badge tier={lead.tier} /></td>
                      <td className="px-4 py-3 text-right"><p className="text-sm font-medium text-[#111]">{lead.score}</p><p className="text-xs text-[#9AADA9]">pts</p></td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1 justify-end" onClick={e => e.stopPropagation()}>
                          <button onClick={() => handleAction(lead.id, "contacted")} title="Mark contacted" className="w-6 h-6 flex items-center justify-center text-[#9AADA9] hover:text-[#166534] rounded hover:bg-[#EDF2F1] text-xs transition-colors">✓</button>
                          <button onClick={() => handleAction(lead.id, "new", { snooze_days: 30 })} title="Snooze 30 days" className="w-6 h-6 flex items-center justify-center text-[#9AADA9] hover:text-[#92400E] rounded hover:bg-[#EDF2F1] text-xs transition-colors">~</button>
                          <button onClick={() => handleAction(lead.id, "dismissed")} title="Dismiss" className="w-6 h-6 flex items-center justify-center text-[#9AADA9] hover:text-[#B91C1C] rounded hover:bg-[#EDF2F1] text-xs transition-colors">✕</button>
                        </div>
                      </td>
                    </tr>
                    {is && (
                      <tr>
                        <td colSpan={9} className="p-0">
                          <Detail lead={lead} close={() => setSel(null)} onAction={handleAction} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          </div>

          {/* Mobile cards */}
          <div className="sm:hidden divide-y divide-[#D8E5E2]">
            {loading ? (
              <div className="px-4 py-8 text-center text-[#9AADA9] text-sm">Loading leads...</div>
            ) : sorted.length === 0 ? (
              <div className="px-4 py-8 text-center text-[#9AADA9] text-sm">No leads match your filters</div>
            ) : sorted.map(lead => {
              const is = sel && sel.id === lead.id;
              const legacyCms = getLegacyCms(lead);
              const sizeDisplay = getSizeDisplay(lead);
              const otherSignals = (lead.signals || []).filter(s => s.type !== "legacy_cms");
              let cmsColor = "text-[#8AA09C]";
              const cmsLower = (legacyCms || "").toLowerCase();
              if (cmsLower.includes("drupal 7") || cmsLower.includes("drupal 6")) cmsColor = "text-[#B91C1C]";
              else if (cmsLower.includes("drupal 8") || cmsLower.includes("drupal 9")) cmsColor = "text-[#9A3412]";
              else if (cmsLower.includes("wordpress")) cmsColor = "text-[#92400E]";
              else if (cmsLower.includes("joomla")) cmsColor = "text-[#C2410C]";
              return (
                <React.Fragment key={lead.id}>
                  <div onClick={() => setSel(is ? null : lead)}
                    className={"p-4 cursor-pointer transition-colors " + (is ? "bg-[#EBF7F4]" : "hover:bg-[#F7FAFA]")}>
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-[#111] text-sm">{lead.company.name}</p>
                        <p className="text-xs text-[#8AA09C]">{lead.company.domain}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge tier={lead.tier} />
                        <span className="text-xs text-[#8AA09C]">{lead.score}pts</span>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {legacyCms && <span className={"text-xs " + cmsColor}>⚠ {legacyCms}</span>}
                      {otherSignals.slice(0, 2).map((s, i) => {
                        const c = SC[s.type] || {};
                        return <span key={i} className={"text-xs " + (c.c || "text-[#6B8480]")}>{c.i} {s.type.replace(/_/g, " ").replace(/\b\w/g, x => x.toUpperCase())}</span>;
                      })}
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex gap-3 text-xs text-[#8AA09C]">
                        {sizeDisplay && <span>{sizeDisplay.label} {sizeDisplay.sublabel}</span>}
                        {lead.company.location && <span>{lead.company.location}</span>}
                      </div>
                      <div className="flex gap-2" onClick={e => e.stopPropagation()}>
                        <button onClick={() => handleAction(lead.id, "contacted")} className="text-[#9AADA9] hover:text-[#166534] text-sm">✓</button>
                        <button onClick={() => handleAction(lead.id, "new", { snooze_days: 30 })} className="text-[#9AADA9] hover:text-[#92400E] text-sm">~</button>
                        <button onClick={() => handleAction(lead.id, "dismissed")} className="text-[#9AADA9] hover:text-[#B91C1C] text-sm">✕</button>
                      </div>
                    </div>
                  </div>
                  {is && <Detail lead={lead} close={() => setSel(null)} onAction={handleAction} />}
                </React.Fragment>
              );
            })}
          </div>
        </div>
    </div>
  );
}

// ─── TARGETS TAB ──────────────────────────────────────────────────────────

function TargetsTab() {
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sf, setSf] = useState(null);
  const [vf, setVf] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [sortCol, setSortCol] = useState("priority");
  const [sortDir, setSortDir] = useState("asc");
  const [filterCms, setFilterCms] = useState("");
  const [filterSource, setFilterSource] = useState("");
  const [filterPriority, setFilterPriority] = useState("");
  const [filterScanned, setFilterScanned] = useState("");
  const PAGE_SIZE = 50;
  const [preview, setPreview] = useState(null);
  const [man, setMan] = useState(false);
  const [form, setForm] = useState({ domain: "", name: "", industry: "", priority: "normal" });
  const [confirming, setConfirming] = useState(false);
  const fr = useRef();

  const fetchTargets = useCallback(() => {
    setLoading(true);
    const params = { page, page_size: PAGE_SIZE, sort_by: sortCol, sort_dir: sortDir };
    if (sf) params.status = sf;
    if (q) params.search = q;
    if (vf) params.vertical = vf;
    if (filterCms) params.cms_filter = filterCms;
    if (filterSource) params.source_filter = filterSource;
    if (filterPriority) params.priority_filter = filterPriority;
    if (filterScanned) params.scanned_filter = filterScanned;
    getTargets(params).then(data => {
      const t = data.targets || data || [];
      setTargets(t);
      setTotal(data.total || t.length);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [sf, q, vf, page, sortCol, sortDir, filterCms, filterSource, filterPriority, filterScanned]);

  useEffect(() => { fetchTargets(); }, [fetchTargets]);
  useEffect(() => { setPage(1); }, [sf, q, vf, sortCol, filterCms, filterSource, filterPriority, filterScanned]);

  function handleSort(col) {
    if (sortCol === col) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortCol(col);
      setSortDir("asc");
    }
  }

  async function handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    const data = await previewCsvUpload(file);
    setPreview(data);
    fr.current.value = "";
  }

  async function handleConfirm() {
    if (!preview) return;
    setConfirming(true);
    await confirmCsvUpload(preview.valid_rows || [], preview.exclude_domains || []);
    setPreview(null);
    setConfirming(false);
    fetchTargets();
  }

  async function handleStatusChange(id, status) {
    await updateTarget(id, { status });
    fetchTargets();
  }

  async function handleManualAdd() {
    if (!form.domain.trim()) return;
    await addManualTarget({ ...form, employee_count: null });
    setForm({ domain: "", name: "", industry: "", priority: "normal" });
    setMan(false);
    fetchTargets();
  }

  const totalPages = Math.ceil(total / PAGE_SIZE);

  async function exportCsv() {
    // Fetch all matching targets (no pagination) for export
    const API = process.env.REACT_APP_API_URL || "";
    const params = new URLSearchParams({ page: 1, page_size: 10000 });
    if (sf) params.append("status", sf);
    if (q) params.append("search", q);
    if (vf) params.append("vertical", vf);
    params.append("sort_by", sortCol);
    params.append("sort_dir", sortDir);

    try {
      const resp = await fetch(`${API}/api/targets?${params}`);
      const data = await resp.json();
      const rows = data.targets || [];

      const headers = ["Domain", "Company Name", "Vertical", "Legacy CMS", "Priority", "Status", "Location", "Source", "Last Scanned"];
      const csvRows = [
        headers.join(","),
        ...rows.map(t => [
          t.domain,
          `"${(t.name || "").replace(/"/g, '""')}"`,
          VL[t.industry] || t.industry || "",
          `"${(t.legacy_cms || "").replace(/"/g, '""')}"`,
          t.priority || "",
          t.status || "",
          `"${(t.location || "").replace(/"/g, '""')}"`,
          t.source || "",
          t.scanned != null ? `${t.scanned}d ago` : "never",
        ].join(","))
      ];

      const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const date = new Date().toISOString().split("T")[0];
      a.href = url;
      a.download = `target-companies-${date}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Export failed", e);
    }
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <p className="text-sm font-medium text-[#2D3A38] mr-1">Target Companies</p>
        <input ref={fr} type="file" accept=".csv" className="hidden" onChange={handleFileChange} />
        <button onClick={() => fr.current && fr.current.click()} className="px-3 py-1.5 text-xs bg-[#0D8A72] text-[#111] rounded hover:bg-[#0D8A72] transition-colors">Upload CSV</button>
        <button onClick={() => downloadCsvTemplate()} className="px-3 py-1.5 text-xs border border-[#C8DAD6] text-[#6B8480] rounded hover:text-[#111] transition-colors">↓ Template</button>
        <button onClick={() => setMan(true)} className="px-3 py-1.5 text-xs border border-[#C8DAD6] text-[#6B8480] rounded hover:text-[#111] transition-colors">+ Add</button>
        <button onClick={exportCsv} className="px-3 py-1.5 text-xs border border-[#C8DAD6] text-[#6B8480] rounded hover:text-[#111] transition-colors">↓ Export</button>
        <span className="text-xs text-[#9AADA9] ml-auto">{total.toLocaleString()} total</span>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="flex rounded-lg border border-[#D8E5E2] overflow-hidden">
          {[{ id: null, l: "All" }, { id: "active", l: "Active" }, { id: "pending_review", l: "Pending" }, { id: "excluded", l: "Excluded" }].map(s => (
            <button key={String(s.id)} onClick={() => setSf(s.id)}
              className={"px-3 py-1.5 text-xs transition-colors " + (sf === s.id ? "bg-[#0D8A72] text-[#111]" : "text-[#6B8480] hover:text-[#111] hover:bg-[#F7FAFA]")}>
              {s.l}
            </button>
          ))}
        </div>
        <select value={vf} onChange={e => setVf(e.target.value)}
          className="px-2 py-1.5 text-xs bg-white border border-[#D8E5E2] rounded text-[#2D3A38] focus:outline-none">
          <option value="">All Verticals</option>
          {VERTS.map(v => <option key={v.id} value={v.id}>{v.l}</option>)}
        </select>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search domain or name..."
          className="px-3 py-1.5 text-xs bg-white border border-[#D8E5E2] rounded text-[#2D3A38] placeholder-[#9AADA9] focus:outline-none w-48" />
      </div>

      {preview && (
        <div className="mb-5 border border-[#C8DAD6] rounded-lg p-4 bg-white/50">
          <p className="text-sm font-medium text-[#111] mb-3">Upload Preview</p>
          <div className="grid grid-cols-4 gap-3 mb-4">
            {[["Will add", preview.summary.will_add, "text-[#166534]"], ["Duplicates", preview.summary.duplicates, "text-[#92400E]"], ["Will exclude", preview.summary.will_exclude, "text-[#9A3412]"], ["Errors", preview.summary.errors, "text-[#B91C1C]"]].map(pair => (
              <div key={pair[0]} className="text-center p-3 bg-[#F7FAFA] rounded-lg">
                <p className={"text-xl font-bold " + pair[2]}>{pair[1]}</p>
                <p className="text-xs text-[#8AA09C] mt-0.5">{pair[0]}</p>
              </div>
            ))}
          </div>
          {preview.errors && preview.errors.map((e, i) => (
            <p key={i} className="text-xs text-[#B91C1C] mb-1">Row {e.row}: {e.domain} - {e.error}</p>
          ))}
          <div className="flex gap-3 mt-3">
            <button onClick={handleConfirm} disabled={confirming || preview.summary.will_add === 0}
              className="px-4 py-2 text-sm bg-green-700 text-[#111] rounded hover:bg-green-600 disabled:opacity-40 transition-colors">
              {confirming ? "Importing..." : "Confirm Import (" + preview.summary.will_add + " domains)"}
            </button>
            <button onClick={() => setPreview(null)} className="px-4 py-2 text-sm border border-[#C8DAD6] text-[#6B8480] rounded hover:text-[#111] transition-colors">Cancel</button>
          </div>
        </div>
      )}

      {/* Filter By section */}
      <div className="bg-white/40 border border-[#D8E5E2] rounded-lg p-3 mb-4">
        <div className="flex items-center justify-between mb-2.5">
          <p className="text-xs font-medium text-[#8AA09C] uppercase tracking-wider">Filter By</p>
          {(sf || vf || filterCms || filterSource || filterPriority || filterScanned || q) && (
            <button onClick={() => { setSf(null); setVf(""); setFilterCms(""); setFilterSource(""); setFilterPriority(""); setFilterScanned(""); setQ(""); }}
              className="text-xs text-[#0D8A72] hover:text-[#0A6B58] transition-colors">
              Clear all
            </button>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
          {/* Status */}
          <div>
            <label className="text-xs text-[#9AADA9] block mb-1">Status</label>
            <select value={sf || ""} onChange={e => setSf(e.target.value || null)}
              className="w-full px-2 py-1.5 text-xs bg-white border border-[#C8DAD6] rounded text-[#2D3A38] focus:outline-none">
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="pending_review">Pending</option>
              <option value="paused">Paused</option>
              <option value="excluded">Excluded</option>
              <option value="contacted">Contacted</option>
              <option value="converted">Converted</option>
            </select>
          </div>
          {/* Vertical */}
          <div>
            <label className="text-xs text-[#9AADA9] block mb-1">Vertical</label>
            <select value={vf} onChange={e => setVf(e.target.value)}
              className="w-full px-2 py-1.5 text-xs bg-white border border-[#C8DAD6] rounded text-[#2D3A38] focus:outline-none">
              <option value="">All verticals</option>
              {VERTS.map(v => <option key={v.id} value={v.id}>{v.l}</option>)}
            </select>
          </div>
          {/* Priority */}
          <div>
            <label className="text-xs text-[#9AADA9] block mb-1">Priority</label>
            <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)}
              className="w-full px-2 py-1.5 text-xs bg-white border border-[#C8DAD6] rounded text-[#2D3A38] focus:outline-none">
              <option value="">All priorities</option>
              <option value="high">High</option>
              <option value="normal">Normal</option>
              <option value="low">Low</option>
            </select>
          </div>
          {/* Source */}
          <div>
            <label className="text-xs text-[#9AADA9] block mb-1">Source</label>
            <select value={filterSource} onChange={e => setFilterSource(e.target.value)}
              className="w-full px-2 py-1.5 text-xs bg-white border border-[#C8DAD6] rounded text-[#2D3A38] focus:outline-none">
              <option value="">All sources</option>
              <option value="ipeds">IPEDS (Higher Ed)</option>
              <option value="manual">Manual</option>
              <option value="csv">CSV Upload</option>
              <option value="prospect_search">Prospect Search</option>
              <option value="news_monitor">News Monitor</option>
              <option value="job_monitor">Job Monitor</option>
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {/* Legacy CMS */}
          <div>
            <label className="text-xs text-[#9AADA9] block mb-1">Legacy CMS</label>
            <select value={filterCms} onChange={e => setFilterCms(e.target.value)}
              className="w-full px-2 py-1.5 text-xs bg-white border border-[#C8DAD6] rounded text-[#2D3A38] focus:outline-none">
              <option value="">All / Unknown</option>
              <option value="drupal7">Drupal 7</option>
              <option value="drupal6">Drupal 6</option>
              <option value="drupal8">Drupal 8</option>
              <option value="drupal9">Drupal 9</option>
              <option value="wordpress">WordPress</option>
              <option value="joomla">Joomla</option>
              <option value="detected">Any detected</option>
              <option value="none">Not yet scanned</option>
            </select>
          </div>
          {/* Scanned */}
          <div>
            <label className="text-xs text-[#9AADA9] block mb-1">Last Scanned</label>
            <select value={filterScanned} onChange={e => setFilterScanned(e.target.value)}
              className="w-full px-2 py-1.5 text-xs bg-white border border-[#C8DAD6] rounded text-[#2D3A38] focus:outline-none">
              <option value="">Any time</option>
              <option value="never">Never scanned</option>
              <option value="today">Today</option>
              <option value="week">This week</option>
              <option value="month">This month</option>
              <option value="older">Over 30 days ago</option>
            </select>
          </div>
          {/* Search */}
          <div>
            <label className="text-xs text-[#9AADA9] block mb-1">Search</label>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Domain or name..."
              className="w-full px-2 py-1.5 text-xs bg-white border border-[#C8DAD6] rounded text-[#2D3A38] placeholder-[#9AADA9] focus:outline-none" />
          </div>
        </div>
      </div>

      <div className="border border-[#D8E5E2] rounded-lg overflow-hidden overflow-x-auto">
        <table className="w-full text-sm min-w-[700px]">
          <thead>
            <tr className="border-b border-[#D8E5E2] bg-white/50">
              {[
                { key: "name", label: "Domain" },
                { key: "industry", label: "Vertical" },
                { key: "legacy_cms", label: "Legacy CMS" },
                { key: "source", label: "Source" },
                { key: "priority", label: "Priority" },
                { key: "status", label: "Status" },
                { key: "last_scanned", label: "Scanned" },
                { key: null, label: "" },
              ].map(col => (
                <th key={col.label}
                  onClick={() => col.key && handleSort(col.key)}
                  className={"text-left px-4 py-2.5 text-xs text-[#8AA09C] font-medium " + (col.key ? "cursor-pointer hover:text-[#2D3A38] select-none" : "")}>
                  {col.label}
                  {col.key && (
                    sortCol === col.key
                      ? <span className="text-[#0D8A72] ml-1">{sortDir === "asc" ? "↑" : "↓"}</span>
                      : <span className="text-[#9AADA9] ml-1">⇅</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-[#9AADA9] text-sm">Loading...</td></tr>
            ) : targets.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-[#9AADA9] text-sm">No targets match your filters</td></tr>
            ) : targets.map(t => {
              const cms = t.legacy_cms;
              let cmsColor = "text-[#9AADA9]";
              const cmsLower = (cms || "").toLowerCase();
              if (cmsLower.includes("drupal 7") || cmsLower.includes("drupal 6")) cmsColor = "text-[#B91C1C]";
              else if (cmsLower.includes("drupal 8") || cmsLower.includes("drupal 9")) cmsColor = "text-[#9A3412]";
              else if (cmsLower.includes("wordpress")) cmsColor = "text-[#92400E]";
              else if (cmsLower.includes("joomla")) cmsColor = "text-[#C2410C]";

              return (
                <tr key={t.id} className="border-b border-[#D8E5E2]/50 hover:bg-white/30">
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium text-[#111]">{t.name || t.domain}</p>
                    {t.name && <p className="text-xs text-[#8AA09C]">{t.domain}</p>}
                    {t.location && <p className="text-xs text-[#9AADA9]">{t.location}</p>}
                  </td>
                  <td className="px-4 py-3 text-xs text-[#6B8480]">{t.industry ? (VL[t.industry] || t.industry) : "—"}</td>
                  <td className="px-4 py-3">
                    {cms
                      ? <span className={"text-xs font-medium " + cmsColor}>⚠ {cms}</span>
                      : <span className="text-xs text-[#9AADA9]">—</span>
                    }
                  </td>
                  <td className="px-4 py-3 text-xs text-[#8AA09C]">{t.source}</td>
                  <td className={"px-4 py-3 text-xs capitalize " + (t.priority === "high" ? "text-[#B91C1C]" : t.priority === "low" ? "text-[#9AADA9]" : "text-[#6B8480]")}>{t.priority}</td>
                  <td className="px-4 py-3">
                    <span className={"text-xs px-2 py-0.5 rounded border capitalize " + (TS[t.status] || "text-[#6B8480]")}>{t.status ? t.status.replace(/_/g, " ") : ""}</span>
                  </td>
                  <td className="px-4 py-3 text-xs text-[#8AA09C]">{ago(t.scanned)}</td>
                  <td className="px-4 py-3">
                    <select value={t.status} onChange={e => handleStatusChange(t.id, e.target.value)}
                      className="text-xs bg-[#F7FAFA] border border-[#C8DAD6] rounded px-2 py-1 text-[#2D3A38] focus:outline-none">
                      <option value="active">Active</option>
                      <option value="pending_review">Pending</option>
                      <option value="paused">Paused</option>
                      <option value="excluded">Excluded</option>
                      <option value="contacted">Contacted</option>
                      <option value="converted">Converted</option>
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-xs text-[#9AADA9]">
            Showing {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, total)} of {total.toLocaleString()}
          </p>
          <div className="flex gap-1">
            <button onClick={() => setPage(1)} disabled={page === 1}
              className="px-2 py-1 text-xs text-[#8AA09C] hover:text-[#111] disabled:opacity-30 transition-colors">«</button>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="px-3 py-1 text-xs bg-[#F7FAFA] border border-[#C8DAD6] rounded text-[#2D3A38] hover:bg-[#EDF2F1] disabled:opacity-30 transition-colors">Prev</button>
            {/* Page number buttons — show up to 5 around current page */}
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const start = Math.max(1, Math.min(page - 2, totalPages - 4));
              const p = start + i;
              return p <= totalPages ? (
                <button key={p} onClick={() => setPage(p)}
                  className={"px-3 py-1 text-xs rounded border transition-colors " +
                    (p === page ? "bg-[#0D8A72] border-[#9FD9CE] text-[#111]" : "bg-[#F7FAFA] border-[#C8DAD6] text-[#2D3A38] hover:bg-[#EDF2F1]")}>
                  {p}
                </button>
              ) : null;
            })}
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="px-3 py-1 text-xs bg-[#F7FAFA] border border-[#C8DAD6] rounded text-[#2D3A38] hover:bg-[#EDF2F1] disabled:opacity-30 transition-colors">Next</button>
            <button onClick={() => setPage(totalPages)} disabled={page === totalPages}
              className="px-2 py-1 text-xs text-[#8AA09C] hover:text-[#111] disabled:opacity-30 transition-colors">»</button>
          </div>
        </div>
      )}

      {man && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-white border border-[#D8E5E2] rounded-lg p-6 w-full max-w-md">
            <p className="text-sm font-medium text-[#111] mb-4">Add Target Manually</p>
            <div className="space-y-3">
              {[["Domain *", "domain", "example.com"], ["Company Name", "name", "Acme Corp"]].map(pair => (
                <div key={pair[1]}>
                  <label className="block text-xs text-[#8AA09C] mb-1">{pair[0]}</label>
                  <input value={form[pair[1]] || ""} onChange={e => setForm(f => ({ ...f, [pair[1]]: e.target.value }))} placeholder={pair[2]}
                    className="w-full px-3 py-2 text-sm bg-[#F7FAFA] border border-[#C8DAD6] rounded text-[#2D3A38] focus:outline-none placeholder-[#9AADA9]" />
                </div>
              ))}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-[#8AA09C] mb-1">Vertical</label>
                  <select value={form.industry} onChange={e => setForm(f => ({ ...f, industry: e.target.value }))}
                    className="w-full px-3 py-2 text-sm bg-[#F7FAFA] border border-[#C8DAD6] rounded text-[#2D3A38] focus:outline-none">
                    <option value="">-- Select --</option>
                    {VERTS.map(v => <option key={v.id} value={v.id}>{v.l}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-[#8AA09C] mb-1">Priority</label>
                  <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}
                    className="w-full px-3 py-2 text-sm bg-[#F7FAFA] border border-[#C8DAD6] rounded text-[#2D3A38] focus:outline-none">
                    <option value="high">High</option>
                    <option value="normal">Normal</option>
                    <option value="low">Low</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={handleManualAdd} disabled={!form.domain.trim()}
                className="flex-1 py-2 text-sm bg-[#0D8A72] text-[#111] rounded hover:bg-[#0D8A72] disabled:opacity-40 transition-colors">Add Target</button>
              <button onClick={() => setMan(false)} className="px-4 py-2 text-sm border border-[#C8DAD6] text-[#6B8480] rounded hover:text-[#111] transition-colors">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── PIPELINE TAB ─────────────────────────────────────────────────────────

function PipelineTab() {
  const [jobs, setJobs] = useState({});
  const [runs, setRuns] = useState([]);
  const [pipelineStatus, setPipelineStatus] = useState({});
  const [scheds, setScheds] = useState({});
  const [running, setRunning] = useState(null);
  const [sel, setSel] = useState(null);
  const [selRunData, setSelRunData] = useState(null);
  const [es, setEs] = useState(null);
  const [ef, setEf] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    getPipelineJobs().then(setJobs);
    getSchedules().then(setScheds);
    fetchStatus();
  }, []);

  function fetchStatus() {
    Promise.all([getPipelineStatus(), getPipelineRuns({ limit: 20 })]).then(([status, runsData]) => {
      setPipelineStatus(status);
      setRuns(Array.isArray(runsData) ? runsData : []);
    });
  }

  useEffect(() => {
    if (running) {
      pollRef.current = setInterval(fetchStatus, 3000);
    } else {
      clearInterval(pollRef.current);
    }
    return () => clearInterval(pollRef.current);
  }, [running]);

  useEffect(() => {
    if (!sel) { setSelRunData(null); return; }
    getPipelineRun(sel).then(setSelRunData);
    const interval = setInterval(() => {
      getPipelineRun(sel).then(data => {
        setSelRunData(data);
        if (data.status !== "running" && data.status !== "pending") clearInterval(interval);
      });
    }, 2000);
    return () => clearInterval(interval);
  }, [sel]);

  async function handleTrigger(jn) {
    if (running) return;
    setRunning(jn);
    const data = await triggerJob(jn);
    setSel(data.run_id);
    fetchStatus();
    setTimeout(() => { setRunning(null); fetchStatus(); }, 5000);
  }

  async function handleSaveSched() {
    if (!es || !ef) return;
    await updateSchedule(es, ef);
    setScheds(s => ({ ...s, [es]: { ...ef, description: sdesc(ef) } }));
    setEs(null);
    setEf(null);
  }

  return (
    <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
      <div className="w-full sm:w-56 shrink-0 space-y-2.5">
        <p className="text-sm font-medium text-[#2D3A38] mb-3">Controls</p>
        {JOB_ORDER.map(jn => {
          const j = jobs[jn] || { label: jn, icon: "⚙", estimated_minutes: 5 };
          const isr = running === jn;
          const full = jn === "full_pipeline";
          const ps = pipelineStatus[jn];

          // Format last run timestamp
          let lastRunDisplay = null;
          let lastRunTitle = null;
          if (ps && ps.last_run_at) {
            const d = new Date(ps.last_run_at);
            const now = new Date();
            const diffMs = now - d;
            const diffMins = Math.floor(diffMs / 60000);
            const diffHours = Math.floor(diffMs / 3600000);
            const diffDays = Math.floor(diffMs / 86400000);
            if (diffMins < 2) lastRunDisplay = "just now";
            else if (diffMins < 60) lastRunDisplay = `${diffMins}m ago`;
            else if (diffHours < 24) lastRunDisplay = `${diffHours}h ago`;
            else lastRunDisplay = `${diffDays}d ago`;
            lastRunTitle = d.toLocaleString();
          } else if (ps && ps.last_run) {
            lastRunDisplay = ps.last_run;
          }

          return (
            <div key={jn} className={"border rounded-lg p-3 " + (full ? "border-[#9FD9CE]/50 bg-[#EBF7F4]/10" : "border-[#D8E5E2] bg-white/20")}>
              <div className="flex justify-between items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-1">
                    <span>{j.icon || j.i}</span>
                    <span className="text-sm font-medium text-[#111]">{j.label || j.l}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {lastRunDisplay && (
                      <span className="text-xs text-[#9AADA9]" title={lastRunTitle || ""}>
                        Last run: <span className="text-[#8AA09C]">{lastRunDisplay}</span>
                      </span>
                    )}
                    {!lastRunDisplay && <span className="text-xs text-[#9AADA9]">Never run</span>}
                    {ps && ps.has_error && <span className="text-xs text-[#B91C1C]">● failed</span>}
                    {ps && ps.last_duration && <span className="text-xs text-[#9AADA9]">{dur(ps.last_duration)}</span>}
                  </div>
                </div>
                <button onClick={() => handleTrigger(jn)} disabled={!!running}
                  className={"shrink-0 px-2.5 py-1 text-xs rounded disabled:opacity-40 transition-colors " + (full ? "bg-[#0D8A72] text-[#111] hover:bg-[#0D8A72]" : "border border-[#C8DAD6] text-[#6B8480] hover:text-[#111] hover:border-[#B0C8C4]")}>
                  {isr ? <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[#0D8A72] animate-pulse" />Running</span> : "Run Now"}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex-1 min-w-0 space-y-6">
        {selRunData && (
          <div className="border border-[#D8E5E2] rounded-lg overflow-hidden">
            <div className="flex justify-between items-center px-4 py-3 border-b border-[#D8E5E2]">
              <div className="flex items-center gap-3">
                <span>{jobs[selRunData.job_name] ? (jobs[selRunData.job_name].icon || jobs[selRunData.job_name].i) : "⚙"}</span>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-[#111]">{jobs[selRunData.job_name] ? (jobs[selRunData.job_name].label || jobs[selRunData.job_name].l) : selRunData.job_name}</span>
                    <RBadge status={selRunData.status} />
                  </div>
                  <p className="text-xs text-[#9AADA9] mt-0.5">by {selRunData.triggered_by}{selRunData.duration_seconds ? " - " + dur(selRunData.duration_seconds) : ""}</p>
                </div>
              </div>
              <button onClick={() => setSel(null)} className="text-[#9AADA9] hover:text-[#2D3A38]">x</button>
            </div>
            {selRunData.stats && Object.keys(selRunData.stats).length > 0 && (
              <div className="px-4 py-3 border-b border-[#D8E5E2] flex flex-wrap gap-2">
                {Object.entries(selRunData.stats).map(function (entry) {
                  const k = entry[0], v = entry[1];
                  if (typeof v === "object") return null;
                  const color = k === "errors" && v > 0 ? "text-[#B91C1C]" : k === "hot" ? "text-[#B91C1C]" : k === "warm" ? "text-[#92400E]" : k === "new_leads" ? "text-[#166534]" : "text-[#6B8480]";
                  return <span key={k} className={"text-xs px-2 py-0.5 rounded bg-[#F7FAFA] border border-[#C8DAD6] " + color}>{k.replace(/_/g, " ")}: {v}</span>;
                })}
              </div>
            )}
            <div className="px-4 py-3 font-mono text-xs space-y-1 h-24 overflow-y-auto">
              {(selRunData.log_lines || []).map((line, i) => {
                const entry = typeof line === "string" ? JSON.parse(line) : line;
                const levelColor = entry.level === "error" ? "text-[#B91C1C]" : entry.level === "warn" ? "text-[#92400E]" : "text-[#6B8480]";
                return (
                  <div key={i} className="flex gap-3">
                    <span className="text-[#9AADA9] shrink-0">{new Date(entry.ts).toLocaleTimeString()}</span>
                    <span className={levelColor}>{entry.msg}</span>
                  </div>
                );
              })}
              {selRunData.status === "running" && (
                <div className="flex gap-3">
                  <span className="text-[#9AADA9] shrink-0">{new Date().toLocaleTimeString()}</span>
                  <span className="text-[#0D8A72]">Running...</span>
                </div>
              )}
              {selRunData.status === "failed" && selRunData.error && (
                <p className="text-[#B91C1C]">{selRunData.error.split("\n")[0]}</p>
              )}
            </div>
          </div>
        )}

        <div>
          <p className="text-xs font-medium text-[#8AA09C] uppercase tracking-wider mb-3">Run History</p>
          <div className="border border-[#D8E5E2] rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#D8E5E2] bg-white/50">
                  {["Job", "Status", "By", "Duration", ""].map(h => (
                    <th key={h} className="text-left px-4 py-2.5 text-xs text-[#8AA09C] font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {runs.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-[#9AADA9] text-sm">No runs yet. Trigger a job to get started.</td></tr>
                ) : runs.map(r => (
                  <tr key={r.id} onClick={() => setSel(sel === r.id ? null : r.id)}
                    className={"border-b border-[#D8E5E2]/50 cursor-pointer transition-colors " + (sel === r.id ? "bg-[#EBF7F4]/20" : "hover:bg-white/40")}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span>{jobs[r.job_name] ? (jobs[r.job_name].icon || jobs[r.job_name].i) : "⚙"}</span>
                        <span className="text-[#2D3A38] text-xs">{jobs[r.job_name] ? (jobs[r.job_name].label || jobs[r.job_name].l) : r.job_name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3"><RBadge status={r.status} /></td>
                    <td className="px-4 py-3 text-xs text-[#8AA09C] capitalize">{r.triggered_by}</td>
                    <td className="px-4 py-3 text-xs text-[#8AA09C]">{dur(r.duration_seconds) || "--"}</td>
                    <td className="px-4 py-3 text-xs text-[#0D8A72]">{sel === r.id ? "^" : ">"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <p className="text-xs font-medium text-[#8AA09C] uppercase tracking-wider mb-1">Schedules</p>
          <p className="text-xs text-[#9AADA9] mb-4">Changes apply within 60 seconds.</p>
          <div className="space-y-2">
            {JOB_ORDER.map(jn => {
              const sc = scheds[jn];
              if (!sc) return null;
              const j = jobs[jn] || { label: jn, icon: "⚙" };
              const ie = es === jn;
              const full = jn === "full_pipeline";
              return (
                <div key={jn} className={"border rounded-lg overflow-hidden " + (full ? "border-[#9FD9CE]/30" : "border-[#D8E5E2]")}>
                  <div className={"flex items-center gap-3 px-4 py-3 " + (full ? "bg-[#EBF7F4]/10" : "bg-white/20")}>
                    <Tog on={sc.enabled} set={async v => {
                      const updated = { ...sc, enabled: v };
                      await updateSchedule(jn, updated);
                      setScheds(s => ({ ...s, [jn]: { ...updated, description: sdesc(updated) } }));
                    }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs">{j.icon || j.i}</span>
                        <span className="text-sm font-medium text-[#111]">{j.label || j.l}</span>
                        {!sc.enabled && <span className="text-xs text-[#9AADA9]">disabled</span>}
                      </div>
                      <p className="text-xs text-[#8AA09C] mt-0.5">{sc.description || sc.desc}</p>
                    </div>
                    <button onClick={() => { if (ie) { setEs(null); setEf(null); } else { setEs(jn); setEf({ ...sc }); } }}
                      className={"px-2.5 py-1 text-xs rounded border transition-colors " + (ie ? "border-[#9FD9CE] text-[#0D8A72]" : "border-[#C8DAD6] text-[#8AA09C] hover:text-[#2D3A38]")}>
                      {ie ? "Cancel" : "Edit"}
                    </button>
                  </div>
                  {ie && ef && (
                    <div className="px-4 py-4 border-t border-[#D8E5E2] bg-[#F2F5F4]/50 space-y-3">
                      <div className="flex items-center gap-3">
                        <label className="text-xs text-[#8AA09C] w-20">Frequency</label>
                        <div className="flex rounded-lg border border-[#C8DAD6] overflow-hidden">
                          {[{ id: "daily", l: "Daily" }, { id: "weekdays", l: "Weekdays" }, { id: "weekly", l: "Selected days" }].map(o => (
                            <button key={o.id} onClick={() => setEf(f => ({ ...f, frequency: o.id, freq: o.id }))}
                              className={"px-3 py-1.5 text-xs transition-colors " + ((ef.frequency || ef.freq) === o.id ? "bg-[#0D8A72] text-[#111]" : "text-[#6B8480] hover:text-[#111] hover:bg-[#F7FAFA]")}>{o.l}</button>
                          ))}
                        </div>
                      </div>
                      {(ef.frequency || ef.freq) === "weekly" && (
                        <div className="flex items-center gap-3">
                          <label className="text-xs text-[#8AA09C] w-20">Days</label>
                          <div className="flex gap-1">
                            {DAYS.map(d => (
                              <button key={d.id} onClick={() => setEf(f => ({ ...f, days: (f.days || []).includes(d.id) ? (f.days || []).filter(x => x !== d.id) : [...(f.days || []), d.id] }))}
                                className={"w-8 h-8 text-xs rounded transition-colors " + ((ef.days || []).includes(d.id) ? "bg-[#0D8A72] text-[#111]" : "bg-[#F7FAFA] text-[#8AA09C] hover:text-[#2D3A38]")}>{d.s}</button>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="flex items-center gap-3">
                        <label className="text-xs text-[#8AA09C] w-20">Time</label>
                        <select value={ef.time} onChange={e => setEf(f => ({ ...f, time: e.target.value }))}
                          className="px-3 py-1.5 text-xs bg-white border border-[#C8DAD6] rounded text-[#2D3A38] focus:outline-none">
                          {TIMES.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                        </select>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-[#8AA09C] w-20">Preview</span>
                        <span className="text-xs text-[#2D3A38]">{sdesc(ef)}</span>
                      </div>
                      <div className="flex gap-3">
                        <button onClick={handleSaveSched} className="px-4 py-1.5 text-xs bg-[#0D8A72] text-[#111] rounded hover:bg-[#0D8A72]">Save</button>
                        <button onClick={() => { setEs(null); setEf(null); }} className="px-4 py-1.5 text-xs border border-[#C8DAD6] text-[#6B8480] rounded hover:text-[#111]">Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── SETTINGS TAB ─────────────────────────────────────────────────────────

function SettingsTab() {
  const [settings, setSettings] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => { getSettings().then(setSettings); }, []);

  function up(k, v) {
    setSettings(s => ({ ...s, [k]: v }));
    setDirty(true);
    setSaved(false);
  }

  async function save() {
    setSaving(true);
    await updateSettings(settings);
    setSaving(false);
    setDirty(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  if (!settings) return <Spinner />;

  const weights = settings.signal_weights || settings.weights || {};
  const thresholds = settings.thresholds || {};
  const filters = settings.firmographic_filters || {};

  return (
    <div className="max-w-2xl space-y-5">
      <div className="flex justify-between items-center">
        <p className="text-sm font-medium text-[#2D3A38]">Pipeline Settings</p>
        <div className="flex items-center gap-3">
          {saved && <span className="text-xs text-[#166534]">Saved</span>}
          <button onClick={save} disabled={!dirty || saving}
            className="px-4 py-2 text-sm bg-[#0D8A72] text-[#111] rounded hover:bg-[#0D8A72] disabled:opacity-40 transition-colors">
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>

      <Box title="Target Verticals" desc="Only companies in selected verticals will be scored.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {VERTS.map(v => (
            <label key={v.id} className="flex items-center gap-2.5 cursor-pointer">
              <input type="checkbox" checked={(settings.verticals || []).includes(v.id)}
                onChange={e => up("verticals", e.target.checked ? [...(settings.verticals || []), v.id] : (settings.verticals || []).filter(x => x !== v.id))}
                className="rounded border-[#B0C8C4] bg-[#F7FAFA] text-[#0D8A72] focus:ring-0" />
              <span className="text-sm text-[#2D3A38] hover:text-[#111]">{v.l}</span>
            </label>
          ))}
        </div>
      </Box>

      <Box title="Score Thresholds" desc="Leads are classified into tiers based on total score.">
        <div className="space-y-3">
          {[{ k: "hot", l: "Hot", c: "text-[#B91C1C]" }, { k: "warm", l: "Warm", c: "text-[#92400E]" }, { k: "cold", l: "Cold", c: "text-[#0D8A72]" }].map(item => (
            <div key={item.k} className="flex items-center gap-4">
              <span className={"text-sm font-medium w-12 " + item.c}>{item.l}</span>
              <span className="text-xs text-[#9AADA9]">min</span>
              <input type="number" min={0} max={200} value={thresholds[item.k] || 0}
                onChange={e => up("thresholds", { ...thresholds, [item.k]: parseInt(e.target.value) || 0 })}
                className="w-20 px-3 py-1.5 text-sm bg-white border border-[#C8DAD6] rounded text-[#2D3A38] focus:outline-none text-center" />
              <span className="text-xs text-[#9AADA9]">points</span>
            </div>
          ))}
        </div>
      </Box>

      <Box title="Signal Weights" desc="Adjust how much each signal type contributes to the score.">
        <div className="space-y-3">
          {Object.entries(SIG_LABELS).map(function (entry) {
            const k = entry[0], l = entry[1];
            const c = SC[k] || {};
            const val = weights[k] !== undefined ? weights[k] : 0;
            return (
              <div key={k} className="flex items-center gap-3">
                <span className={"text-xs w-5 " + (c.c || "")}>{c.i}</span>
                <span className="text-sm text-[#2D3A38] flex-1">{l}</span>
                <input type="range" min={0} max={50} value={val}
                  onChange={e => up("signal_weights", { ...weights, [k]: parseInt(e.target.value) })}
                  className="w-28 accent-blue-500" />
                <span className="text-sm text-[#6B8480] w-6 text-right">{val}</span>
              </div>
            );
          })}
        </div>
      </Box>

      <Box title="Firmographic Filters" desc="Companies outside these ranges are deprioritized.">
        <div className="grid grid-cols-2 gap-4">
          {[["Min Employees", "employee_min"], ["Max Employees", "employee_max"]].map(pair => (
            <div key={pair[1]}>
              <label className="text-xs text-[#8AA09C] block mb-1">{pair[0]}</label>
              <input type="number" value={filters[pair[1]] !== null && filters[pair[1]] !== undefined ? filters[pair[1]] : ""}
                onChange={e => up("firmographic_filters", { ...filters, [pair[1]]: parseInt(e.target.value) || null })}
                className="w-full px-3 py-1.5 text-sm bg-white border border-[#C8DAD6] rounded text-[#2D3A38] focus:outline-none" />
            </div>
          ))}
        </div>
      </Box>

      <Box title="Geographic Focus" desc="Leave empty for global coverage.">
        <Tags tags={settings.geographic_focus || []} set={v => up("geographic_focus", v)} ph="Add region" />
      </Box>
      <Box title="Custom Keywords" desc="Additional terms for news and job monitoring.">
        <Tags tags={settings.custom_keywords || []} set={v => up("custom_keywords", v)} ph="Add keyword" />
      </Box>
      <Box title="Competitor Exclusions" desc="Suppress leads working with these vendors.">
        <Tags tags={settings.competitor_exclusions || []} set={v => up("competitor_exclusions", v)} ph="Add competitor" />
      </Box>
      <Box title="Excluded Domains" desc="Permanently suppress specific domains.">
        <Tags tags={settings.excluded_domains || []} set={v => up("excluded_domains", v)} ph="Add domain" />
      </Box>
    </div>
  );
}

// ─── ROOT APP ─────────────────────────────────────────────────────────────

export default function App() {
  const [tab, setTab] = useState("leads");
  const [stats, setStats] = useState(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Prospect search state lifted here so results persist across tab switches
  const [prospectResults, setProspectResults] = useState([]);
  const [prospectSearched, setProspectSearched] = useState(false);

  useEffect(() => {
    getStats().then(setStats);
    const interval = setInterval(() => getStats().then(setStats), 60000);
    return () => clearInterval(interval);
  }, []);

  const hot = stats ? stats.leads.hot : 0;
  const warm = stats ? stats.leads.warm : 0;
  const total = stats ? (stats.leads.hot + stats.leads.warm + stats.leads.cold) : 0;
  const activeTargets = stats ? stats.targets.active : 0;

  const TABS = [
    { id: "leads", l: "Leads" },
    { id: "targets", l: "Target Companies" },
    { id: "prospects", l: "🔍 Find Prospects" },
    { id: "network", l: "🤝 Network" },
    { id: "pipeline", l: "Pipeline" },
    { id: "settings", l: "Settings" },
  ];

  function switchTab(id) {
    setTab(id);
    setMobileNavOpen(false);
  }

  return (
    <div className="min-h-screen bg-[#F2F5F4] text-[#111]">
      {/* Header */}
      <header className="border-b border-[#E0E8E5] px-4 py-3 bg-white">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div>
            <p className="text-sm font-semibold text-[#111]">Lead Pipeline</p>
            <p className="text-xs text-[#9AADA9] hidden sm:block">Drupal &amp; WordPress Agency</p>
          </div>
          {/* Stats — compact on mobile */}
          <div className="flex items-center gap-3 sm:gap-5">
            {[["Hot", hot, "text-[#B91C1C]"], ["Warm", warm, "text-[#92400E]"], ["Total", total, "text-[#6B8480]"]].map(item => (
              <div key={item[0]} className="text-center">
                <p className={"text-base sm:text-lg font-bold " + item[2]}>{item[1]}</p>
                <p className="text-xs text-[#9AADA9]">{item[0]}</p>
              </div>
            ))}
            <div className="w-px h-6 bg-[#F7FAFA] hidden sm:block" />
            <div className="text-center hidden sm:block">
              <p className="text-lg font-bold text-[#6B8480]">{activeTargets.toLocaleString()}</p>
              <p className="text-xs text-[#9AADA9]">Targets</p>
            </div>
            {/* Mobile hamburger */}
            <button onClick={() => setMobileNavOpen(o => !o)}
              className="ml-2 p-2 text-[#6B8480] hover:text-[#111] sm:hidden">
              {mobileNavOpen ? "✕" : "☰"}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile nav dropdown */}
      {mobileNavOpen && (
        <div className="sm:hidden border-b border-[#E0E8E5] bg-white px-4 py-2">
          {TABS.map(t => (
            <button key={t.id} onClick={() => switchTab(t.id)}
              className={"block w-full text-left px-3 py-2.5 text-sm rounded transition-colors " +
                (tab === t.id ? "text-[#0D8A72] bg-[#E6F6F3]" : "text-[#6B8480] hover:text-[#111] hover:bg-[#F7FAFA]")}>
              {t.l}
            </button>
          ))}
        </div>
      )}

      {/* Desktop nav */}
      <div className="border-b border-[#E0E8E5] px-4 hidden sm:block bg-white">
        <div className="max-w-7xl mx-auto flex gap-1 overflow-x-auto">
          {TABS.map(t => (
            <button key={t.id} onClick={() => switchTab(t.id)}
              className={"py-3 px-3 text-sm whitespace-nowrap border-b-2 transition-colors " +
                (tab === t.id ? "border-[#0D8A72] text-[#0D8A72]" : "border-transparent text-[#8AA09C] hover:text-[#2D3A38]")}>
              {t.l}
            </button>
          ))}
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-5">
        {tab === "leads" && <LeadsTab />}
        {tab === "targets" && <TargetsTab />}
        {tab === "prospects" && <ProspectsTab results={prospectResults} setResults={setProspectResults} searched={prospectSearched} setSearched={setProspectSearched} />}
        {tab === "network" && <NetworkTab />}
        {tab === "pipeline" && <PipelineTab />}
        {tab === "settings" && <SettingsTab />}
      </main>
    </div>
  );
}

// ─── PROSPECTS TAB ────────────────────────────────────────────────────────

const US_INDUSTRIES = [
  { id: "b2b_manufacturing", l: "Manufacturing & Wholesale" },
  { id: "b2b_professional_services", l: "Professional Services" },
  { id: "b2b_saas", l: "Enterprise Software (B2B)" },
  { id: "healthcare", l: "Healthcare" },
  { id: "nonprofit", l: "Nonprofit / Association" },
  { id: "media_publishing", l: "Media & Publishing" },
  { id: "financial_services", l: "Financial Services" },
  { id: "higher_education", l: "Higher Education" },
];

function ProspectsTab({ results, setResults, searched, setSearched }) {
  const [zipCodes, setZipCodes] = useState("");
  const [industries, setIndustries] = useState([]);
  const [employeeMin, setEmployeeMin] = useState("");
  const [keywords, setKeywords] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [addedDomains, setAddedDomains] = useState(new Set());
  const [addError, setAddError] = useState(null);
  const API = process.env.REACT_APP_API_URL || "";

  async function handleSearch() {
    const zips = zipCodes.split(/[\s,]+/).map(z => z.trim()).filter(Boolean);
    if (!zips.length) { setMessage("Enter at least one zip code"); return; }
    setLoading(true); setSearched(true); setMessage(null); setResults([]); setSelectedDomains(new Set());
    try {
      const resp = await fetch(`${API}/api/prospects/search`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          zip_codes: zips,
          industries: industries.length ? industries : null,
          employee_min: employeeMin ? parseInt(employeeMin) : null,
          keywords: keywords || null,
        }),
      });
      const data = await resp.json();
      if (data.setup_required) {
        setMessage(data.message);
        setResults([]);
      } else {
        setResults(data.results || []);
      }
    } catch (e) {
      setMessage("Search failed — check API connection");
    }
    setLoading(false);
  }

  const [selectedDomains, setSelectedDomains] = useState(new Set());
  const [bulkAdding, setBulkAdding] = useState(false);

  const [expandedCompany, setExpandedCompany] = useState(null);

  async function addToTargets(company) {
    setAddError(null);
    try {
      const resp = await fetch(`${API}/api/prospects/add-to-pipeline`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(company),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        setAddError(err.detail || "Failed to add company");
        return;
      }
      setAddedDomains(prev => new Set([...prev, company.domain]));
    } catch (e) {
      setAddError("Network error — could not add company");
    }
  }

  async function addAllSelected() {
    setBulkAdding(true);
    setAddError(null);
    const toAdd = results.filter(c => c.domain && selectedDomains.has(c.domain) && !addedDomains.has(c.domain));
    let added = 0;
    for (const company of toAdd) {
      try {
        const resp = await fetch(`${API}/api/prospects/add-to-pipeline`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(company),
        });
        if (resp.ok) {
          setAddedDomains(prev => new Set([...prev, company.domain]));
          added++;
        }
      } catch (e) { console.error(e); }
    }
    setSelectedDomains(new Set());
    setBulkAdding(false);
    if (added < toAdd.length) setAddError(`Added ${added} of ${toAdd.length} — some failed`);
  }

  function exportResultsCsv() {
    const headers = ["Company Name", "Domain", "Address", "Phone", "Industry", "Employees", "Revenue"];
    const rows = [
      headers.join(","),
      ...results.map(c => [
        `"${(c.name || "").replace(/"/g, '""')}"`,
        c.domain || "",
        `"${(c.address || c.location || "").replace(/"/g, '""')}"`,
        c.phone || "",
        VL[c.industry] || c.industry || "",
        c.employee_count || "",
        c.revenue_estimate || "",
      ].join(","))
    ];
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const date = new Date().toISOString().split("T")[0];
    a.href = url;
    a.download = `prospect-search-${date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function toggleSelect(domain) {
    setSelectedDomains(prev => {
      const next = new Set(prev);
      next.has(domain) ? next.delete(domain) : next.add(domain);
      return next;
    });
  }

  function toggleSelectAll() {
    const selectable = results.filter(c => c.domain && !addedDomains.has(c.domain)).map(c => c.domain);
    if (selectedDomains.size === selectable.length) {
      setSelectedDomains(new Set());
    } else {
      setSelectedDomains(new Set(selectable));
    }
  }

  function toggleIndustry(id) {
    setIndustries(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  }

  return (
    <div className="max-w-5xl">
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-[#111] mb-1">Find Prospects</h2>
        <p className="text-sm text-[#8AA09C]">Search for established B2B companies by location. Add promising ones to Target Companies for ongoing monitoring.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
        <div className="sm:col-span-2">
          <label className="text-xs text-[#8AA09C] block mb-1.5">Zip Codes <span className="text-[#9AADA9]">(comma or space separated)</span></label>
          <input
            value={zipCodes}
            onChange={e => setZipCodes(e.target.value)}
            placeholder="92612, 90210, 92660"
            className="w-full px-3 py-2 text-sm bg-white border border-[#C8DAD6] rounded text-[#2D3A38] placeholder-[#9AADA9] focus:outline-none focus:border-[#0D8A72]"
          />
        </div>
        <div>
          <label className="text-xs text-[#8AA09C] block mb-1.5">Keywords <span className="text-[#9AADA9]">(optional)</span></label>
          <input
            value={keywords}
            onChange={e => setKeywords(e.target.value)}
            placeholder="e.g. wholesale, logistics"
            className="w-full px-3 py-2 text-sm bg-white border border-[#C8DAD6] rounded text-[#2D3A38] placeholder-[#9AADA9] focus:outline-none focus:border-[#0D8A72]"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="text-xs text-[#8AA09C] block mb-1.5">Min Employees <span className="text-[#9AADA9]">(optional)</span></label>
          <input type="number" value={employeeMin} onChange={e => setEmployeeMin(e.target.value)}
            placeholder="e.g. 50"
            className="w-full px-3 py-2 text-sm bg-white border border-[#C8DAD6] rounded text-[#2D3A38] focus:outline-none" />
        </div>
        <div>
          <label className="text-xs text-[#8AA09C] block mb-1.5">Narrow by industry <span className="text-[#9AADA9]">(optional)</span></label>
          <div className="flex flex-wrap gap-1.5">
            {US_INDUSTRIES.map(ind => (
              <button key={ind.id} onClick={() => toggleIndustry(ind.id)}
                className={"px-2 py-1 text-xs rounded border transition-colors " +
                  (industries.includes(ind.id)
                    ? "bg-[#E6F6F3] border-[#9FD9CE] text-[#0A6B58]"
                    : "bg-white border-[#C8DAD6] text-[#8AA09C] hover:text-[#2D3A38]")}>
                {ind.l}
              </button>
            ))}
          </div>
        </div>
      </div>

      <button onClick={handleSearch} disabled={loading}
        className="px-5 py-2 text-sm bg-[#0D8A72] hover:bg-[#0A6B58] disabled:opacity-40 text-[#111] rounded font-medium transition-colors mb-6">
        {loading ? "Searching... (this may take 30-60 seconds)" : "Search Companies"}
      </button>

      {addError && (
        <div className="mb-3 px-3 py-2 bg-[#FEF2F2] border border-[#FCA5A5] rounded text-xs text-red-300">
          {addError}
        </div>
      )}

      {message && (
        <div className="mb-4 p-4 bg-[#FFFBEB] border border-[#FCD34D] rounded text-sm text-[#92400E]">
          <p className="font-medium mb-1">Setup Required</p>
          <p>{message}</p>
        </div>
      )}

      {searched && !loading && !message && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-[#9AADA9]">{results.length} companies found</p>
            {results.length > 0 && (
              <div className="flex gap-2">
                {selectedDomains.size > 0 && (
                  <button onClick={addAllSelected} disabled={bulkAdding}
                    className="px-3 py-1.5 text-xs bg-[#0D8A72] hover:bg-[#0A6B58] disabled:opacity-40 text-[#111] rounded transition-colors">
                    {bulkAdding ? "Adding..." : `+ Add ${selectedDomains.size} to Targets`}
                  </button>
                )}
                <button onClick={exportResultsCsv}
                  className="px-3 py-1.5 text-xs border border-[#C8DAD6] text-[#6B8480] hover:text-[#111] rounded transition-colors">
                  ↓ Export CSV
                </button>
              </div>
            )}
          </div>

          {results.length > 0 && (
            <div className="flex items-center gap-2 mb-2 px-1">
              <input type="checkbox"
                checked={selectedDomains.size === results.filter(c => c.domain && !addedDomains.has(c.domain)).length && results.length > 0}
                onChange={toggleSelectAll}
                className="rounded border-[#B0C8C4] bg-[#F7FAFA] text-[#0D8A72]" />
              <span className="text-xs text-[#9AADA9]">
                {selectedDomains.size > 0 ? `${selectedDomains.size} selected` : "Select all"}
              </span>
            </div>
          )}

          <div className="space-y-2">
            {results.map((company, i) => {
              const isExpanded = expandedCompany === i;
              return (
                <div key={i} className={"border rounded-lg transition-colors " +
                  (selectedDomains.has(company.domain) ? "border-[#9FD9CE] bg-[#EBF7F4]/10" : "border-[#D8E5E2] bg-white hover:border-[#C8DAD6]")}>
                  {/* Main row */}
                  <div className="flex items-start gap-3 p-3">
                    {company.domain && !addedDomains.has(company.domain) && (
                      <input type="checkbox"
                        checked={selectedDomains.has(company.domain)}
                        onChange={() => toggleSelect(company.domain)}
                        onClick={e => e.stopPropagation()}
                        className="rounded border-[#B0C8C4] bg-[#F7FAFA] text-[#0D8A72] mt-1 shrink-0" />
                    )}
                    {(!company.domain || addedDomains.has(company.domain)) && (
                      <div className="w-4 shrink-0" />
                    )}
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setExpandedCompany(isExpanded ? null : i)}>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-[#111]">{company.name}</p>
                        <span className="text-xs text-[#9AADA9]">{isExpanded ? "▲" : "▼"}</span>
                      </div>
                      <p className="text-xs text-[#8AA09C]">{company.domain}{company.location ? " · " + company.location : ""}</p>
                      {!isExpanded && company.description && (
                        <p className="text-xs text-[#9AADA9] mt-0.5 truncate">{company.description}</p>
                      )}
                    </div>
                    <div className="text-right shrink-0 min-w-32">
                      {company.employee_count && <p className="text-xs text-[#2D3A38]">{company.employee_count.toLocaleString()} emp</p>}
                      {company.revenue_estimate && <p className="text-xs text-[#166534]">{company.revenue_estimate}</p>}
                      {company.industry && <p className="text-xs text-[#9AADA9]">{VL[company.industry] || company.industry}</p>}
                    </div>
                    {company.domain && (
                      addedDomains.has(company.domain) ? (
                        <span className="text-xs text-[#166534] px-3 py-1.5 shrink-0 self-center">✓ Added</span>
                      ) : (
                        <button onClick={e => { e.stopPropagation(); addToTargets(company); }}
                          className="px-3 py-1.5 text-xs bg-[#E6F6F3] hover:bg-[#D0EDE9] text-[#0A6B58] rounded border border-[#9FD9CE] transition-colors shrink-0 self-center whitespace-nowrap">
                          + Add
                        </button>
                      )
                    )}
                  </div>

                  {/* Expanded detail panel */}
                  {isExpanded && (
                    <div className="border-t border-[#D8E5E2] px-4 py-3 grid grid-cols-2 gap-x-8 gap-y-2">
                      {[
                        ["Domain", company.domain && <a href={`https://${company.domain}`} target="_blank" rel="noopener noreferrer" className="text-[#0D8A72] hover:text-[#0A6B58]">{company.domain}</a>],
                        ["Address", company.address],
                        ["Phone", company.phone],
                        ["Industry", VL[company.industry] || company.industry],
                        ["Employees", company.employee_count ? company.employee_count.toLocaleString() : null],
                        ["Revenue", company.revenue_estimate],
                        ["Description", company.description],
                        ["Place Types", company.place_types && company.place_types.length > 0 ? company.place_types.join(", ").replace(/_/g, " ") : null],
                        ["LinkedIn", company.linkedin_url && <a href={company.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-[#0D8A72] hover:text-[#0A6B58]">View profile</a>],
                        ["Keywords", company.keywords && company.keywords.length > 0 ? company.keywords.join(", ") : null],
                      ].filter(([, v]) => v).map(([label, value]) => (
                        <div key={label} className="flex gap-3">
                          <span className="text-xs text-[#9AADA9] w-20 shrink-0">{label}</span>
                          <span className="text-xs text-[#2D3A38]">{value}</span>
                        </div>
                      ))}
                      {!addedDomains.has(company.domain) && (
                        <div className="col-span-2 pt-2 border-t border-[#D8E5E2] mt-1">
                          <button onClick={() => addToTargets(company)}
                            className="px-4 py-1.5 text-xs bg-[#0D8A72] hover:bg-[#0A6B58] text-[#111] rounded transition-colors">
                            + Add to Target Companies
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {results.length === 0 && (
              <p className="text-sm text-[#9AADA9] py-4 text-center">No companies found. Try different zip codes or add specific keywords.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── NETWORK TAB ──────────────────────────────────────────────────────────

const RELATIONSHIP_COLORS = {
  hot: "text-[#B91C1C] bg-[#FEF2F2] border-[#FCA5A5]",
  warm: "text-[#92400E] bg-[#FFFBEB] border-[#FCD34D]",
  cold: "text-[#0D8A72] bg-[#EBF7F4] border-[#9FD9CE]",
};

const SIGNAL_LABELS_NET = {
  leadership_change: { l: "Leadership Change", i: "👤", c: "text-[#6B21A8]" },
  funding_event: { l: "Funding", i: "💰", c: "text-[#166534]" },
  b2b_rebrand: { l: "Rebrand", i: "🎨", c: "text-pink-400" },
  org_change: { l: "Org Change", i: "🔀", c: "text-[#92400E]" },
  strategy_announced: { l: "Strategy", i: "📋", c: "text-cyan-400" },
  b2b_expansion: { l: "Expansion", i: "🌍", c: "text-teal-400" },
  pain_signal: { l: "Pain Signal", i: "😤", c: "text-[#C2410C]" },
  general_news: { l: "News", i: "📰", c: "text-[#6B8480]" },
};

function NetworkTab() {
  const [contacts, setContacts] = useState([]);
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showCsv, setShowCsv] = useState(false);
  const [csvPreview, setCsvPreview] = useState(null);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvConfirming, setCsvConfirming] = useState(false);
  const [form, setForm] = useState({
    name: "", title: "", company_name: "", domain: "",
    email: "", relationship_strength: "warm", how_known: "",
    location: "", notes: "", monitor_signals: true,
  });
  const [activeView, setActiveView] = useState("contacts");
  const csvRef = useRef();
  const API = process.env.REACT_APP_API_URL || "";

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [cr, sr] = await Promise.all([
        fetch(`${API}/api/network/contacts`).then(r => r.json()),
        fetch(`${API}/api/network/signals?days=30`).then(r => r.json()),
      ]);
      setContacts(cr.contacts || []);
      setSignals(sr.signals || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [API]);

  useEffect(() => { fetchData(); }, [fetchData]);

  async function saveContact() {
    try {
      await fetch(`${API}/api/network/contacts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      setForm({ name: "", title: "", company_name: "", domain: "", email: "", relationship_strength: "warm", how_known: "", location: "", notes: "", monitor_signals: true });
      setShowForm(false);
      fetchData();
    } catch (e) { console.error(e); }
  }

  async function deleteContact(id) {
    if (!window.confirm("Remove this contact from monitoring?")) return;
    await fetch(`${API}/api/network/contacts/${id}`, { method: "DELETE" });
    fetchData();
  }

  async function runMonitor() {
    await fetch(`${API}/api/network/run`, { method: "POST" });
    setTimeout(fetchData, 3000);
  }

  function downloadCsvTemplate() {
    const headers = "name,title,company_name,domain,email,relationship_strength,how_known,location,notes";
    const example = "Kevin Smith,CEO,Acme Corp,acme.com,kevin@acme.com,warm,Met at DrupalCon 2024,Orange County CA,Interested in headless CMS";
    const blob = new Blob([headers + "\n" + example], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "network_contacts_template.csv";
    a.click(); URL.revokeObjectURL(url);
  }

  function parseCsvContacts(text) {
    const lines = text.trim().split("\n");
    if (lines.length < 2) return [];
    const headers = lines[0].split(",").map(h => h.trim().toLowerCase().replace(/\s+/g, "_"));
    const contacts = [];
    for (let i = 1; i < lines.length; i++) {
      const vals = lines[i].split(",").map(v => v.trim().replace(/^"|"$/g, ""));
      if (!vals[0]) continue;
      const obj = {};
      headers.forEach((h, idx) => { obj[h] = vals[idx] || ""; });
      contacts.push({
        name: obj.name || "",
        title: obj.title || "",
        company_name: obj.company_name || obj.company || "",
        domain: obj.domain || "",
        email: obj.email || "",
        relationship_strength: obj.relationship_strength || obj.relationship || "warm",
        how_known: obj.how_known || obj.how_you_know_them || "",
        location: obj.location || "",
        notes: obj.notes || "",
        monitor_signals: true,
      });
    }
    return contacts;
  }

  async function handleCsvFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    setCsvUploading(true);
    const text = await file.text();
    const parsed = parseCsvContacts(text);
    // Check for duplicates
    const existingEmails = new Set(contacts.map(c => c.email).filter(Boolean));
    const existingNames = new Set(contacts.map(c => c.name.toLowerCase()));
    const preview = parsed.map(c => ({
      ...c,
      duplicate: (c.email && existingEmails.has(c.email)) ||
                 existingNames.has(c.name.toLowerCase()),
    }));
    setCsvPreview(preview);
    setCsvUploading(false);
    if (csvRef.current) csvRef.current.value = "";
  }

  async function confirmCsvImport() {
    if (!csvPreview) return;
    setCsvConfirming(true);
    const toImport = csvPreview.filter(c => !c.duplicate && c.name);
    let imported = 0;
    for (const contact of toImport) {
      try {
        await fetch(`${API}/api/network/contacts`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(contact),
        });
        imported++;
      } catch (e) { console.error(e); }
    }
    setCsvPreview(null);
    setCsvConfirming(false);
    setShowCsv(false);
    fetchData();
    alert(`Imported ${imported} contacts successfully.`);
  }

  return (
    <div className="max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-[#111] mb-1">Network Monitoring</h2>
          <p className="text-sm text-[#8AA09C]">Monitor your existing relationships for buying signals. Get alerted when contacts change jobs, raise money, or announce new initiatives.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={runMonitor} className="px-3 py-1.5 text-xs bg-[#F7FAFA] hover:bg-[#EDF2F1] text-[#2D3A38] rounded border border-[#C8DAD6] transition-colors">
            Run Monitor Now
          </button>
          <button onClick={downloadCsvTemplate} className="px-3 py-1.5 text-xs bg-[#F7FAFA] hover:bg-[#EDF2F1] text-[#2D3A38] rounded border border-[#C8DAD6] transition-colors">
            ↓ CSV Template
          </button>
          <button onClick={() => { setShowCsv(!showCsv); setShowForm(false); }}
            className="px-3 py-1.5 text-xs bg-[#EDF2F1] hover:bg-gray-600 text-[#111] rounded border border-[#B0C8C4] transition-colors">
            Upload CSV
          </button>
          <button onClick={() => { setShowForm(!showForm); setShowCsv(false); setCsvPreview(null); }}
            className="px-3 py-1.5 text-xs bg-[#0D8A72] hover:bg-[#0A6B58] text-[#111] rounded transition-colors">
            + Add Contact
          </button>
        </div>
      </div>

      {/* CSV Upload */}
      {showCsv && !csvPreview && (
        <div className="mb-6 p-4 bg-white border border-[#C8DAD6] rounded-lg">
          <p className="text-sm font-medium text-[#111] mb-1">Upload Contacts CSV</p>
          <p className="text-xs text-[#8AA09C] mb-3">
            Required column: <code className="bg-[#F7FAFA] px-1 rounded">name</code>.
            Optional: <code className="bg-[#F7FAFA] px-1 rounded">title</code>, <code className="bg-[#F7FAFA] px-1 rounded">company_name</code>, <code className="bg-[#F7FAFA] px-1 rounded">domain</code>, <code className="bg-[#F7FAFA] px-1 rounded">email</code>, <code className="bg-[#F7FAFA] px-1 rounded">relationship_strength</code>, <code className="bg-[#F7FAFA] px-1 rounded">how_known</code>, <code className="bg-[#F7FAFA] px-1 rounded">location</code>, <code className="bg-[#F7FAFA] px-1 rounded">notes</code>
          </p>
          <input ref={csvRef} type="file" accept=".csv" onChange={handleCsvFile} disabled={csvUploading}
            className="block w-full text-sm text-[#6B8480] file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-xs file:bg-[#EDF2F1] file:text-[#2D3A38] hover:file:bg-gray-600 file:cursor-pointer" />
          {csvUploading && <p className="text-xs text-[#8AA09C] mt-2">Parsing CSV...</p>}
        </div>
      )}

      {/* CSV Preview */}
      {csvPreview && (
        <div className="mb-6 p-4 bg-white border border-[#C8DAD6] rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-sm font-medium text-[#111]">Preview — {csvPreview.filter(c => !c.duplicate).length} contacts to import</p>
              {csvPreview.filter(c => c.duplicate).length > 0 && (
                <p className="text-xs text-yellow-500 mt-0.5">{csvPreview.filter(c => c.duplicate).length} duplicates will be skipped</p>
              )}
            </div>
            <div className="flex gap-2">
              <button onClick={() => { setCsvPreview(null); setShowCsv(false); }}
                className="px-3 py-1.5 text-xs text-[#6B8480] hover:text-[#111] transition-colors">Cancel</button>
              <button onClick={confirmCsvImport} disabled={csvConfirming || csvPreview.filter(c => !c.duplicate).length === 0}
                className="px-4 py-1.5 text-xs bg-[#0D8A72] hover:bg-[#0A6B58] disabled:opacity-40 text-[#111] rounded transition-colors">
                {csvConfirming ? "Importing..." : `Import ${csvPreview.filter(c => !c.duplicate).length} Contacts`}
              </button>
            </div>
          </div>
          <div className="border border-[#D8E5E2] rounded overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-[#F7FAFA] border-b border-[#C8DAD6]">
                  <th className="px-3 py-2 text-left text-[#8AA09C] font-medium">Name</th>
                  <th className="px-3 py-2 text-left text-[#8AA09C] font-medium">Title</th>
                  <th className="px-3 py-2 text-left text-[#8AA09C] font-medium">Company</th>
                  <th className="px-3 py-2 text-left text-[#8AA09C] font-medium">Relationship</th>
                  <th className="px-3 py-2 text-left text-[#8AA09C] font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {csvPreview.slice(0, 20).map((c, i) => (
                  <tr key={i} className={"border-b border-[#D8E5E2] " + (c.duplicate ? "opacity-40" : "")}>
                    <td className="px-3 py-2 text-[#2D3A38]">{c.name}</td>
                    <td className="px-3 py-2 text-[#8AA09C]">{c.title}</td>
                    <td className="px-3 py-2 text-[#8AA09C]">{c.company_name}</td>
                    <td className="px-3 py-2 text-[#8AA09C]">{c.relationship_strength}</td>
                    <td className="px-3 py-2">
                      {c.duplicate
                        ? <span className="text-yellow-600">duplicate</span>
                        : <span className="text-green-500">✓ new</span>
                      }
                    </td>
                  </tr>
                ))}
                {csvPreview.length > 20 && (
                  <tr><td colSpan={5} className="px-3 py-2 text-[#9AADA9] text-center">...and {csvPreview.length - 20} more</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showForm && (
        <div className="mb-6 p-4 bg-white border border-[#C8DAD6] rounded-lg">
          <p className="text-sm font-medium text-[#111] mb-3">Add Network Contact</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            {[["Name *", "name"], ["Title", "title"], ["Company", "company_name"], ["Domain", "domain"], ["Email", "email"], ["Location", "location"]].map(([label, field]) => (
              <div key={field}>
                <label className="text-xs text-[#8AA09C] block mb-1">{label}</label>
                <input value={form[field]} onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
                  className="w-full px-3 py-1.5 text-sm bg-[#F7FAFA] border border-[#C8DAD6] rounded text-[#2D3A38] focus:outline-none" />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-xs text-[#8AA09C] block mb-1">Relationship</label>
              <select value={form.relationship_strength} onChange={e => setForm(f => ({ ...f, relationship_strength: e.target.value }))}
                className="w-full px-3 py-1.5 text-sm bg-[#F7FAFA] border border-[#C8DAD6] rounded text-[#2D3A38] focus:outline-none">
                <option value="hot">Hot — Close friend / regular contact</option>
                <option value="warm">Warm — Known, occasional contact</option>
                <option value="cold">Cold — Met once / loose connection</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-[#8AA09C] block mb-1">How do you know them?</label>
              <input value={form.how_known} onChange={e => setForm(f => ({ ...f, how_known: e.target.value }))}
                placeholder="e.g. Met at DrupalCon, boating in OC"
                className="w-full px-3 py-1.5 text-sm bg-[#F7FAFA] border border-[#C8DAD6] rounded text-[#2D3A38] focus:outline-none" />
            </div>
          </div>
          <div className="mb-3">
            <label className="text-xs text-[#8AA09C] block mb-1">Notes</label>
            <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              className="w-full px-3 py-1.5 text-sm bg-[#F7FAFA] border border-[#C8DAD6] rounded text-[#2D3A38] focus:outline-none" />
          </div>
          <div className="flex items-center gap-3 mb-4">
            <input type="checkbox" checked={form.monitor_signals} onChange={e => setForm(f => ({ ...f, monitor_signals: e.target.checked }))}
              id="monitor" className="rounded border-[#B0C8C4] bg-[#F7FAFA] text-[#0D8A72]" />
            <label htmlFor="monitor" className="text-sm text-[#2D3A38]">Monitor for buying signals</label>
          </div>
          <div className="flex gap-2">
            <button onClick={saveContact} disabled={!form.name}
              className="px-4 py-1.5 text-sm bg-[#0D8A72] hover:bg-[#0A6B58] disabled:opacity-40 text-[#111] rounded transition-colors">
              Save Contact
            </button>
            <button onClick={() => setShowForm(false)} className="px-4 py-1.5 text-sm text-[#6B8480] hover:text-[#111] transition-colors">
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="flex gap-1 mb-4 border-b border-[#D8E5E2]">
        {[["contacts", `Contacts (${contacts.length})`], ["signals", `Recent Signals (${signals.length})`]].map(([id, label]) => (
          <button key={id} onClick={() => setActiveView(id)}
            className={"pb-2 px-3 text-sm border-b-2 transition-colors " + (activeView === id ? "border-[#0D8A72] text-[#0D8A72]" : "border-transparent text-[#8AA09C] hover:text-[#2D3A38]")}>
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-sm text-[#9AADA9] py-4">Loading...</p>
      ) : activeView === "contacts" ? (
        <div className="space-y-2">
          {contacts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-[#9AADA9] text-sm mb-2">No contacts yet</p>
              <p className="text-[#9AADA9] text-xs">Add contacts to start monitoring their companies for buying signals</p>
            </div>
          ) : contacts.map(contact => (
            <div key={contact.id} className="flex items-center gap-4 p-3 bg-white border border-[#D8E5E2] rounded-lg hover:border-[#C8DAD6] transition-colors">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <p className="text-sm font-medium text-[#111]">{contact.name}</p>
                  <span className={"text-xs px-1.5 py-0.5 rounded border " + (RELATIONSHIP_COLORS[contact.relationship_strength] || RELATIONSHIP_COLORS.warm)}>
                    {contact.relationship_strength}
                  </span>
                  {contact.monitor_signals && <span className="text-xs text-green-500">● monitoring</span>}
                </div>
                <p className="text-xs text-[#8AA09C]">
                  {contact.title && `${contact.title} · `}{contact.company_name || contact.domain || ""}
                </p>
                {contact.how_known && <p className="text-xs text-[#9AADA9] mt-0.5">{contact.how_known}</p>}
              </div>
              <div className="text-right shrink-0">
                {contact.recent_signals > 0 && (
                  <span className="text-xs text-[#6B21A8]">{contact.recent_signals} signal{contact.recent_signals !== 1 ? "s" : ""} (30d)</span>
                )}
              </div>
              <button onClick={() => deleteContact(contact.id)}
                className="text-[#9AADA9] hover:text-[#B91C1C] text-xs transition-colors shrink-0">×</button>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {signals.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-[#9AADA9] text-sm mb-2">No signals yet</p>
              <p className="text-[#9AADA9] text-xs">Click "Run Monitor Now" to check for recent news about your contacts' companies</p>
            </div>
          ) : signals.map(sig => {
            const sl = SIGNAL_LABELS_NET[sig.signal_type] || { l: sig.signal_type, i: "📰", c: "text-[#6B8480]" };
            return (
              <div key={sig.id} className="p-3 bg-white border border-[#D8E5E2] rounded-lg border-l-2 border-l-purple-700">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={"text-xs font-medium " + sl.c}>{sl.i} {sl.l}</span>
                      <span className="text-xs text-[#9AADA9]">·</span>
                      <span className="text-xs text-[#8AA09C]">{sig.contact_name}</span>
                      {sig.company_name && <span className="text-xs text-[#9AADA9]">at {sig.company_name}</span>}
                    </div>
                    <p className="text-sm text-[#2D3A38]">{sig.detail}</p>
                    {sig.source_url && (
                      <a href={sig.source_url} target="_blank" rel="noopener noreferrer"
                        className="text-xs text-blue-500 hover:text-[#0D8A72] mt-1 inline-block">
                        Read article →
                      </a>
                    )}
                  </div>
                  <p className="text-xs text-[#9AADA9] shrink-0">
                    {sig.detected_at ? new Date(sig.detected_at).toLocaleDateString() : ""}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
